// src/pages/UserAnswersPage.jsx

import React, { useEffect, useState, useRef, useCallback } from 'react'
import html2canvas from 'html2canvas' // Kolajı tek resme dönüştürmek için
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  CircularProgress,
  Radio,
  Checkbox,
  FormControlLabel,
  Snackbar,
  Alert,
  TextField,
  Divider,
  IconButton,
  Backdrop,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Chip,
  Avatar,
  Fab
} from '@mui/material'

import VideocamIcon from '@mui/icons-material/Videocam';
import SendIcon from '@mui/icons-material/Send';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'


import { useTheme, useMediaQuery } from '@mui/material'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { signInAnonymously } from 'firebase/auth'
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  addDoc,
  serverTimestamp,
  doc,
  getDoc,
  updateDoc
} from 'firebase/firestore'
import { auth, db } from '../firebase'
import { useAuth } from '../contexts/AuthContext'
import { normalizePlanData } from '../utils/planUtils'

// Çoklu dil desteği
import { useTranslation } from 'react-i18next'

// ReactQuill Kaldırıldı!
// import ReactQuill from 'react-quill'
// import 'react-quill/dist/quill.snow.css'

// Medya yükleme aracı
import { uploadFile } from '../utils/uploadFile'
import { consumeResponseQuota } from '../utils/planUtils'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import VisibilityIcon from '@mui/icons-material/Visibility'
import CloseIcon from '@mui/icons-material/Close'

import CollageCanvas from '../components/CollageCanvas'

function AiModeratorChat({ question, conversation, onSendMessage, isSubmitting, isAiLoading }) {
  const [userMessage, setUserMessage] = useState('')
  const aiCount = question.aiCount || 0
  const aiCountPlusOne = aiCount + 1
  const { t } = useTranslation()

  // Kaç tane user-rolü mesajı girildiğini sayalım
  const userMessageCount = conversation.filter((m) => m.role === 'user').length

  const handleSend = () => {
    if (!userMessage.trim()) return
    onSendMessage(userMessage.trim())
    setUserMessage('')
  }

  const isInputDisabled =
    isSubmitting || (aiCountPlusOne > 0 && userMessageCount >= aiCountPlusOne)

  const remainingCount = Math.max(aiCountPlusOne - userMessageCount, 0)

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 3,
        p: 2,
        background:
          'linear-gradient(135deg, rgba(248,250,252,0.96), rgba(239,246,255,0.96))',
        borderColor: 'rgba(148,163,184,0.35)'
      }}
    >
      {/* Header - sosyal medya tarzı üst bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1.5
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar
            sx={{
              width: 32,
              height: 32,
              fontSize: 14,
              bgcolor: 'primary.main',
              color: '#fff'
            }}
          >
            AI
          </Avatar>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {t('userAnswersPage.chatScreen')}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {t('userAnswersPage.aiMessagePlaceholder')}
            </Typography>
          </Box>
        </Box>

        {aiCountPlusOne > 0 && (
          <Chip
            size="small"
            variant="outlined"
            label={t('userAnswersPage.aiLimitedPrompt', { count: remainingCount })}
            sx={{ fontWeight: 500 }}
          />
        )}
      </Box>

      {/* Mesajlar alanı */}
      <Box
        sx={{
          borderRadius: 2,
          p: 1.5,
          mb: 1.5,
          maxHeight: 260,
          minHeight: 140,
          overflowY: 'auto',
          background:
            'radial-gradient(circle at top, rgba(191,219,254,0.25), transparent 55%), #f8fafc'
        }}
      >
        {conversation.length === 0 && (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              opacity: 0.75
            }}
          >
            <Typography variant="body2" color="textSecondary">
              {t('userAnswersPage.chatScreen')}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {t('userAnswersPage.aiMessagePlaceholder')}
            </Typography>
          </Box>
        )}

        {conversation
          .filter((msg) => msg.role !== 'question')
          .map((msg, idx) => {
            const isAssistant = msg.role === 'assistant'
            return (
              <Box
                key={idx}
                sx={{
                  display: 'flex',
                  justifyContent: isAssistant ? 'flex-start' : 'flex-end',
                  mb: 1
                }}
              >
                <Box
                  sx={{
                    maxWidth: '75%',
                    px: 1.5,
                    py: 1,
                    borderRadius: 3,
                    bgcolor: isAssistant
                      ? 'rgba(226,232,240,0.95)'
                      : 'rgba(59,130,246,0.96)',
                    color: isAssistant ? 'text.primary' : '#fff',
                    boxShadow: isAssistant
                      ? '0 6px 14px rgba(148,163,184,0.35)'
                      : '0 8px 18px rgba(37,99,235,0.5)'
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      fontSize: 14
                    }}
                  >
                    {msg.content}
                  </Typography>
                </Box>
              </Box>
            )
          })}
      </Box>

      {/* Input alanı - sosyal medya mesaj kutusu */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}
      >
        <Paper
          variant="outlined"
          sx={{
            flexGrow: 1,
            display: 'flex',
            alignItems: 'center',
            px: 1.5,
            py: 0.75,
            borderRadius: 999,
            boxShadow: '0 4px 10px rgba(148,163,184,0.25)',
            bgcolor: '#ffffff'
          }}
        >
          <TextField
            variant="standard"
            fullWidth
            size="small"
            value={userMessage}
            onChange={(e) => setUserMessage(e.target.value)}
            placeholder={t('userAnswersPage.aiMessagePlaceholder')}
            disabled={isInputDisabled}
            InputProps={{
              disableUnderline: true,
              sx: { fontSize: 14 }
            }}
          />
        </Paper>

        <IconButton
          color="primary"
          onClick={handleSend}
          disabled={isInputDisabled}
          sx={{
            borderRadius: '50%',
            boxShadow: '0 8px 18px rgba(37,99,235,0.45)'
          }}
        >
          <SendIcon />
        </IconButton>
      </Box>

      {isAiLoading && (
        <Box mt={1} display="flex" alignItems="center" gap={1}>
          <CircularProgress size={16} />
          <Typography variant="caption" color="textSecondary">
            {t('userAnswersPage.aiWaiting')}
          </Typography>
        </Box>
      )}
    </Paper>
  )
}

export default function UserAnswersPage({ isPublic = false }) {
  const { currentUser, organization, isAnonymousUser } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const { shareId } = useParams()
  const { t, i18n } = useTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const isPublicLink = isPublic || Boolean(shareId)
  const locationState = location.state || {}
  const searchParams = new URLSearchParams(location.search || '')
  const initialChannelId = locationState.channelId || searchParams.get('channelId')
  const initialQuestionFormId = locationState.questionFormId || searchParams.get('questionFormId')
  const previewParam = searchParams.get('preview')
  const initialPreviewMode =
    locationState.preview === true || previewParam === '1' || previewParam === 'true'

  const [channelId, setChannelId] = useState(initialChannelId)
  const [questionFormId, setQuestionFormId] = useState(initialQuestionFormId)
  const [formMeta, setFormMeta] = useState(null)
  const [responderId, setResponderId] = useState(currentUser?.uid || null)
  const [shareLinkError, setShareLinkError] = useState('')
  const [resetToken, setResetToken] = useState(0)
  const [isPreviewMode, setIsPreviewMode] = useState(initialPreviewMode)

  const [questionFormStatus, setQuestionFormStatus] = useState(null)
  const [questions, setQuestions] = useState([])
  const [loadingQuestions, setLoadingQuestions] = useState(false)
  const [hasAnswered, setHasAnswered] = useState(false)
  const [quotaExceeded, setQuotaExceeded] = useState(false)
  const [screenoutResult, setScreenoutResult] = useState(null)
  const [answersState, setAnswersState] = useState({})
  const [activeStep, setActiveStep] = useState(0)
  const [responseDocId, setResponseDocId] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const [aiLoading, setAiLoading] = useState(false)

  // Medya yükleme anında sayfayı kitlemek için
  const [mediaUploading, setMediaUploading] = useState(false)

  const [isResponsesFetched, setIsResponsesFetched] = useState(false)
 
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  })
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false })
  }

  // Kolaj (photoCollage) için state'ler
  const [collageDialogOpen, setCollageDialogOpen] = useState(false)
  const [collageQuestionId, setCollageQuestionId] = useState(null)
  const [collageItems, setCollageItems] = useState([])
  const collageRef = useRef(null)

  // Video kaydı için state'ler
  const [videoDialogOpen, setVideoDialogOpen] = useState(false)
  const [videoQuestionId, setVideoQuestionId] = useState(null)
  const [videoPreviewUrl, setVideoPreviewUrl] = useState(null)
  const [mediaRecorder, setMediaRecorder] = useState(null)
  const [cameraStream, setCameraStream] = useState(null)
  const [recordedChunks, setRecordedChunks] = useState([])
  const [isRecording, setIsRecording] = useState(false)
  const [disableStop, setDisableStop] = useState(false)
  const videoRef = useRef(null)
  const sessionStartRef = useRef(Date.now())

  const [uploadProgress, setUploadProgress] = useState(0); 
  const [skipAnonSignIn, setSkipAnonSignIn] = useState(false)
  const [publicAuthReady, setPublicAuthReady] = useState(!isPublicLink)
  const [isResolvingLink, setIsResolvingLink] = useState(Boolean(shareId))

  const arrangeQuestionsWithInfoPlacement = useCallback((qs = []) => {
    const startScreens = []
    const endScreens = []
    const middle = []

    qs.forEach((q) => {
      const placement = q.questionType === 'infoQuestion' ? q.infoPlacement || 'inline' : 'inline'
      if (q.questionType === 'infoQuestion' && placement === 'start') {
        startScreens.push(q)
      } else if (q.questionType === 'infoQuestion' && placement === 'end') {
        endScreens.push(q)
      } else {
        middle.push(q)
      }
    })

    return [...startScreens, ...middle, ...endScreens]
  }, [])

  useEffect(() => {
    const params = new URLSearchParams(location.search || '')
    const nextChannelId = location.state?.channelId || params.get('channelId')
    const nextQuestionFormId = location.state?.questionFormId || params.get('questionFormId')
    const previewVal = params.get('preview')
    const nextPreview =
      location.state?.preview === true || previewVal === '1' || previewVal === 'true'

    if (nextChannelId && nextChannelId !== channelId) {
      setChannelId(nextChannelId)
    }
    if (nextQuestionFormId && nextQuestionFormId !== questionFormId) {
      setQuestionFormId(nextQuestionFormId)
    }
    setIsPreviewMode(nextPreview)
  }, [location, channelId, questionFormId])

  // Public link için login gereksinimini kaldırmak adına anonim oturum aç
  useEffect(() => {
    if (!isPublicLink) return
    setPublicAuthReady(false)
    if (currentUser) {
      setPublicAuthReady(true)
      return
    }
    if (skipAnonSignIn) {
      setPublicAuthReady(true)
      return
    }
    if (auth.currentUser?.isAnonymous) {
      setPublicAuthReady(true)
      return
    }

    signInAnonymously(auth).catch((err) => {
      if (err?.code === 'auth/admin-restricted-operation' || err?.code === 'auth/operation-not-allowed') {
        // Anonymous login is disabled in Firebase console; continue without it.
        console.warn('Anonymous sign-in disabled by project settings, continuing without auth')
        setSkipAnonSignIn(true)
        setPublicAuthReady(true)
        return
      }

      console.error('Anonymous sign-in failed', err)
      setSnackbar({
        open: true,
        message: t('userAnswersPage.loadingDataError'),
        severity: 'error'
      })
      setPublicAuthReady(true)
    }).then(() => {
      setPublicAuthReady(true)
    })
  }, [isPublicLink, currentUser, shareId, t, skipAnonSignIn])

  // ================================
  // Check channelId & questionFormId
  // ================================
  useEffect(() => {
    if (isPublicLink) return
    if (!channelId || !questionFormId) {
      setSnackbar({
        open: true,
        message: t('userAnswersPage.errorNoChannelOrForm'),
        severity: 'error'
      })
    }
  }, [channelId, questionFormId, isPublicLink, t])

  // Public link üzerinden geliniyorsa formu çözümle
  useEffect(() => {
    if (!shareId) {
      setIsResolvingLink(false)
      return
    }
    if (!publicAuthReady) return

    async function resolveFormByShareId() {
      try {
        setIsResolvingLink(true)
        const qForm = query(
          collection(db, 'questionForms'),
          where('publicShareId', '==', shareId)
        )
        const snap = await getDocs(qForm)

        if (snap.empty) {
          setShareLinkError(t('userAnswersPage.publicLinkNotFound'))
          setIsResolvingLink(false)
          return
        }

        const docSnap = snap.docs[0]
        const data = docSnap.data()
        setChannelId(data.channelId)
        setQuestionFormId(docSnap.id)
        setFormMeta({ id: docSnap.id, ...data })
        setShareLinkError('')
      } catch (err) {
        setShareLinkError(t('userAnswersPage.loadingDataError'))
      } finally {
        setIsResolvingLink(false)
      }
    }

    resolveFormByShareId()
  }, [shareId, t, publicAuthReady])

  // Yanıtlayıcı kimliği: login veya anonim
  useEffect(() => {
    if (currentUser) {
      setResponderId(currentUser.uid)
      return
    }

    if (isAnonymousUser && auth.currentUser?.uid) {
      setResponderId(auth.currentUser.uid)
      return
    }

    if (!shareId) {
      setResponderId(null)
      return
    }

    const storageKey = `publicResponder-${shareId}`
    let storedId = localStorage.getItem(storageKey)
    if (!storedId) {
      storedId = `anon-${shareId}-${Date.now().toString(36)}-${
        Math.random().toString(36).slice(2, 7)
      }`
      localStorage.setItem(storageKey, storedId)
    }
    setResponderId(storedId)
  }, [currentUser, shareId, isAnonymousUser])

  // ================================
  // Fetch form & questions
  // ================================
  useEffect(() => {
    if (!channelId || !questionFormId) return
    const activeUserId = currentUser?.uid || responderId

    async function fetchData() {
      sessionStartRef.current = Date.now()
      try {
        setLoadingQuestions(true)

        // Form dokümanı
        const formDoc = await getDoc(doc(db, 'questionForms', questionFormId))
        if (!formDoc.exists()) {
          setSnackbar({
            open: true,
            message: t('userAnswersPage.loadingDataError'),
            severity: 'error'
          })
          setLoadingQuestions(false)
          return
        }
        const formData = formDoc.data()

        if (!isPublicLink && organization?.id) {
          const belongsToOrganization =
            formData.organizationId && formData.organizationId === organization.id
          const createdByCurrentUser =
            formData.createdBy && formData.createdBy === currentUser?.uid

          if (
            (formData.organizationId && !belongsToOrganization) ||
            (!formData.organizationId && !createdByCurrentUser)
          ) {
            setSnackbar({
              open: true,
              message: 'Bu forma erişim izniniz yok.',
              severity: 'error'
            })
            setLoadingQuestions(false)
            setIsResponsesFetched(true)
            return
          }
        }

        if (
          !formData.organizationId &&
          organization?.id &&
          formData.createdBy &&
          formData.createdBy === currentUser?.uid
        ) {
          await updateDoc(doc(db, 'questionForms', questionFormId), {
            organizationId: organization.id
          })
          formData.organizationId = organization.id
        }

        setQuestionFormStatus(formData.status)
        setFormMeta({ id: questionFormId, ...formData })

        // Kota kontrolü: limit dolduysa yayında değil gibi davran
        const orgIdForQuota = formData.organizationId || organization?.id || null
        if (orgIdForQuota && !isPreviewMode) {
          try {
            const orgSnap = await getDoc(doc(db, 'organizations', orgIdForQuota))
            if (orgSnap.exists()) {
              const plan = normalizePlanData({ id: orgSnap.id, ...orgSnap.data() })
              const remaining = Math.max((plan.responseLimit || 0) - (plan.responsesUsed || 0), 0)
              if (remaining <= 0) {
                setQuotaExceeded(true)
                setHasAnswered(true)
                setIsResponsesFetched(true)
                setLoadingQuestions(false)
                return
              }
            }
          } catch (err) {
            console.error('Quota check error:', err)
          }
        }

        // Yayınlanmamış form
        if (formData.status !== 'published' && !isPreviewMode) {
          setSnackbar({
            open: true,
            message: t('userAnswersPage.errorNotPublished'),
            severity: 'error'
          })
          setLoadingQuestions(false)
          return
        }

        let partialResponseData = null

        if (activeUserId && !isPreviewMode) {
          // Daha önce yanıt vermiş mi
          const respQuery = query(
            collection(db, 'responses'),
            where('userId', '==', activeUserId),
            where('channelId', '==', channelId),
            where('questionFormId', '==', questionFormId)
          )
          const respSnap = await getDocs(respQuery)

          if (!respSnap.empty) {
            const existingDoc = respSnap.docs[0]
            const existingData = existingDoc.data()
            setResponseDocId(existingDoc.id)

            if (existingData.status === 'completed') {
              setHasAnswered(true)
              setLoadingQuestions(false)
              setIsResponsesFetched(true)
              return
            } else if (existingData.status === 'screenedOut') {
              setScreenoutResult({
                message:
                  existingData.screenout?.message ||
                  t('userAnswersPage.screenoutDefaultMessage'),
                choice: existingData.screenout?.choice || '',
                questionId: existingData.screenout?.questionId || null,
                questionText: existingData.screenout?.questionText || ''
              })
              setHasAnswered(true)
              setLoadingQuestions(false)
              setIsResponsesFetched(true)
              return
            } else {
              partialResponseData = existingData
            }
          }
        }

        // Soru dokümanları
        const qQuery = query(
          collection(db, 'questions'),
          where('channelId', '==', channelId),
          where('questionFormId', '==', questionFormId),
          orderBy('sortOrder', 'asc')
        )
        const qSnap = await getDocs(qQuery)
        const qs = qSnap.docs.map((d) => ({ id: d.id, ...d.data() }))
        const orderedQs = arrangeQuestionsWithInfoPlacement(qs)

        setQuestions(orderedQs)

        // answersState başlat
        const initAns = {}
        orderedQs.forEach((q) => {
          switch (q.questionType) {
            case 'openEnded':
              initAns[q.id] = { text: '', media: [] }
              break
            case 'singleChoice':
              if (q.allowOther) {
                initAns[q.id] = {
                  choice: '',
                  otherChecked: false,
                  otherText: ''
                }
              } else {
                initAns[q.id] = ''
              }
              break
            case 'multipleChoice':
              if (q.allowOther) {
                initAns[q.id] = {
                  choices: [],
                  otherChecked: false,
                  otherText: ''
                }
              } else {
                initAns[q.id] = []
              }
              break
            case 'ratingQuestion':
              initAns[q.id] = 0
              break
            case 'videoQuestion':
              initAns[q.id] = { videoUrl: '' }
              break
            case 'photoCollage':
              initAns[q.id] = {
                collageSingleUrl: ''
              }
              break
            case 'aiModerator':
              initAns[q.id] = {
                conversation: []
              }
              break
            case 'infoQuestion':
              initAns[q.id] = null
              break
            case 'cardSorting':
              initAns[q.id] = { sorted: {} };
              break;
            case 'screening':
              initAns[q.id] = q.screeningMode === 'multiple' ? [] : ''
              break
            case 'pictureChoice': {
              if (q.pictureAllowOther) {
                if (q.pictureAllowMultiple) {
                  initAns[q.id] = { choices: [], otherChecked: false, otherText: '' }
                } else {
                  initAns[q.id] = { choice: '', otherChecked: false, otherText: '' }
                }
              } else {
                initAns[q.id] = q.pictureAllowMultiple ? [] : ''
              }
              break
            }
            case 'masked':
              initAns[q.id] = ''
              break
          default:
            initAns[q.id] = null
        }
        })

        // inProgress yanıtları varsa merge et
        if (partialResponseData?.answers) {
          Object.keys(partialResponseData.answers).forEach((qid) => {
            initAns[qid] = partialResponseData.answers[qid]
          })
          if (partialResponseData.progress) {
            setActiveStep(partialResponseData.progress)
          }
        }

        setAnswersState(initAns)
        setLoadingQuestions(false)
        setIsResponsesFetched(true)
      } catch (err) {
        setSnackbar({
          open: true,
          message: t('userAnswersPage.loadingDataError'),
          severity: 'error'
        })
        setLoadingQuestions(false)
        setIsResponsesFetched(true)
      }
    }

    fetchData()
  }, [
    currentUser,
    channelId,
    questionFormId,
    responderId,
    isPublicLink,
    resetToken,
    t,
    organization,
    isPreviewMode,
    arrangeQuestionsWithInfoPlacement
  ])

  useEffect(() => {
    if (!formMeta) return
    const targetLanguage = formMeta.language || 'tr'
    if (i18n.language !== targetLanguage) {
      i18n.changeLanguage(targetLanguage).catch((err) =>
        console.error('Language change failed', err)
      )
    }
  }, [formMeta, i18n])

  // ================================
  // Various answer update helpers
  // ================================
  const handleOpenEndedTextChange = (questionId, content) => {
    setAnswersState((prev) => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        text: content
      }
    }))
  }

  const handleSingleChoiceChange = (questionId, choice) => {
    const qVal = answersState[questionId]
    if (typeof qVal === 'string') {
      setAnswersState((prev) => ({
        ...prev,
        [questionId]: choice
      }))
    } else {
      setAnswersState((prev) => ({
        ...prev,
        [questionId]: {
          ...prev[questionId],
          choice,
          otherChecked: false,
          otherText: ''
        }
      }))
    }
  }

  const handleMultipleChoiceChange = (questionId, choice) => {
    const qVal = answersState[questionId]
    if (Array.isArray(qVal)) {
      // Basit array
      if (qVal.includes(choice)) {
        setAnswersState((prev) => ({
          ...prev,
          [questionId]: qVal.filter((c) => c !== choice)
        }))
      } else {
        setAnswersState((prev) => ({
          ...prev,
          [questionId]: [...prev[questionId], choice]
        }))
      }
    } else {
      // allowOther
      const choicesArr = qVal.choices
      if (choicesArr.includes(choice)) {
        setAnswersState((prev) => ({
          ...prev,
          [questionId]: {
            ...prev[questionId],
            choices: choicesArr.filter((c) => c !== choice)
          }
        }))
      } else {
        setAnswersState((prev) => ({
          ...prev,
          [questionId]: {
            ...prev[questionId],
            choices: [...choicesArr, choice]
          }
        }))
      }
    }
  }

  const handlePictureSingleChange = (question, choice) => {
    const qVal = answersState[question.id]
    if (question.pictureAllowOther && typeof qVal === 'object') {
      setAnswersState((prev) => ({
        ...prev,
        [question.id]: {
          ...prev[question.id],
          choice,
          otherChecked: false,
          otherText: ''
        }
      }))
    } else {
      setAnswersState((prev) => ({
        ...prev,
        [question.id]: choice
      }))
    }
  }

  const handlePictureMultiChange = (question, choice) => {
    const qVal = answersState[question.id]
    if (question.pictureAllowOther && typeof qVal === 'object') {
      const choicesArr = qVal.choices || []
      const exists = choicesArr.includes(choice)
      const updated = exists ? choicesArr.filter((c) => c !== choice) : [...choicesArr, choice]
      setAnswersState((prev) => ({
        ...prev,
        [question.id]: {
          ...prev[question.id],
          choices: updated
        }
      }))
    } else {
      const arr = Array.isArray(qVal) ? qVal : []
      const exists = arr.includes(choice)
      const updated = exists ? arr.filter((c) => c !== choice) : [...arr, choice]
      setAnswersState((prev) => ({
        ...prev,
        [question.id]: updated
      }))
    }
  }

  const handleMaskedChange = (question, value) => {
    setAnswersState((prev) => ({
      ...prev,
      [question.id]: value
    }))
  }

  const handleScreeningSingleChange = (question, choice) => {
    const nextAnswers = {
      ...answersState,
      [question.id]: choice
    }
    setAnswersState(nextAnswers)

    const matched = findScreenoutRule(question, choice)
    if (matched && !screenoutResult) {
      handleScreenoutCompletion(question, matched, nextAnswers)
    }
  }

  const handleScreeningMultipleChange = (question, choice) => {
    const currentVal = Array.isArray(answersState[question.id]) ? answersState[question.id] : []
    const exists = currentVal.includes(choice)
    const updatedChoices = exists
      ? currentVal.filter((c) => c !== choice)
      : [...currentVal, choice]

    const nextAnswers = {
      ...answersState,
      [question.id]: updatedChoices
    }

    setAnswersState(nextAnswers)

    const matched = findScreenoutRule(question, updatedChoices)
    if (matched && !screenoutResult) {
      handleScreenoutCompletion(question, matched, nextAnswers)
    }
  }

  const toggleOtherChecked = (questionId) => {
    setAnswersState((prev) => {
      const val = prev[questionId]
      return {
        ...prev,
        [questionId]: {
          ...val,
          otherChecked: !val.otherChecked
        }
      }
    })
  }

  const handleOtherTextChange = (questionId, text) => {
    setAnswersState((prev) => {
      const val = prev[questionId]
      return {
        ...prev,
        [questionId]: {
          ...val,
          otherText: text
        }
      }
    })
  }

  const handleRatingChange = (questionId, ratingValue) => {
    setAnswersState((prev) => ({
      ...prev,
      [questionId]: ratingValue
    }))
  }

  // ================================
  // OpenEnded: add/remove media
  // ================================
  const handleAddMedia = async (questionId) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*,video/*,audio/*';
    input.multiple = true;
    // Hide the input if you don't want it visible
    input.style.display = 'none';
    
    // Append to DOM so iOS 18 recognizes it as part of the document
    document.body.appendChild(input);
  
    input.onchange = async () => {
      const files = Array.from(input.files || []);
      if (!files.length) return;
  
      setMediaUploading(true);
      setUploadProgress(0);
  
      try {
        const uploadedMedia = [];
        const totalFiles = files.length;
        let completedFiles = 0;
  
        for (let file of files) {
          const remoteUrl = await uploadFile(file);

          completedFiles++;
          setUploadProgress(Math.round((completedFiles / totalFiles) * 100));
  
          const fileType = file.type.startsWith('image/')
            ? 'image'
            : file.type.startsWith('video/')
            ? 'video'
            : file.type.startsWith('audio/')
            ? 'audio'
            : 'file';
  
          uploadedMedia.push({ remoteUrl, fileType });
        }
  
        setAnswersState((prev) => {
          const prevQ = prev[questionId];
          return {
            ...prev,
            [questionId]: {
              ...prevQ,
              media: [...(prevQ.media || []), ...uploadedMedia],
            },
          };
        });
      } catch (err) {
        setSnackbar({
          open: true,
          message: err.message || t('userAnswersPage.videoUploadError'),
          severity: 'error',
        });
      } finally {
        setMediaUploading(false);
        setUploadProgress(0);
        // Remove the input from the DOM once done
        document.body.removeChild(input);
      }
    };
  
    input.click();
  };

  const handleRemoveMedia = (questionId, mediaIndex) => {
    setAnswersState((prev) => {
      const mediaArr = [...(prev[questionId].media || [])]
      mediaArr.splice(mediaIndex, 1)
      return {
        ...prev,
        [questionId]: {
          ...prev[questionId],
          media: mediaArr
        }
      }
    })
  }

  // ================================
  // VIDEO QUESTION Dialog
  // ================================
  const startCameraPreview = async () => {
    try {
      const constraints = {
        audio: true,
        video: true
      }

      const stream = await navigator.mediaDevices.getUserMedia(constraints)
      setCameraStream(stream)

      if (videoRef.current) {
        videoRef.current.srcObject = stream
        videoRef.current.muted = true
        videoRef.current.setAttribute('playsinline', 'true')
        videoRef.current.setAttribute('webkit-playsinline', 'true')
        try {
          await videoRef.current.play()
        } catch (e) {
          // iOS autoplay restriction, ignore
        }
      }
    } catch (error) {
      console.error('Camera preview error', error)
      setSnackbar({
        open: true,
        message: t('userAnswersPage.cameraError'),
        severity: 'error'
      })
    }
  }

  const handleVideoQuestion = (questionId) => {
    setVideoQuestionId(questionId)
    setVideoDialogOpen(true)
    // Dialog açılır açılmaz kamera ön izlemesini başlat
    startCameraPreview()
  }

  const handleToggleRecording = async () => {
    if (!isRecording) {
      // START
      try {
        if (!('MediaRecorder' in window)) {
          setSnackbar({
            open: true,
            message: t('userAnswersPage.noVideoSupport'),
            severity: 'warning'
          })
          return
        }

        let mimeType
        if (window.MediaRecorder.isTypeSupported('video/webm;codecs=vp9')) {
          mimeType = 'video/webm;codecs=vp9'
        } else if (window.MediaRecorder.isTypeSupported('video/webm;codecs=vp8')) {
          mimeType = 'video/webm;codecs=vp8'
        } else if (window.MediaRecorder.isTypeSupported('video/mp4;codecs=h264')) {
          mimeType = 'video/mp4;codecs=h264'
        } else if (
          window.MediaRecorder.isTypeSupported('video/mp4;codecs="avc1.424028, mp4a.40.2"')
        ) {
          mimeType = 'video/mp4;codecs="avc1.424028, mp4a.40.2"'
        } else {
          setSnackbar({
            open: true,
            message: t('userAnswersPage.noCompatibleVideoFormat'),
            severity: 'error'
          })
          return
        }

        // Eğer dialog açıldığında önizleme başlatıldıysa mevcut stream'i kullan
        let stream = cameraStream

        // Herhangi bir sebeple stream yoksa, şimdi başlat
        if (!stream) {
          try {
            await startCameraPreview()
            stream = cameraStream
          } catch (e) {
            // startCameraPreview zaten snackbar basıyor
            return
          }
        }

        if (!stream) {
          setSnackbar({
            open: true,
            message: t('userAnswersPage.cameraError'),
            severity: 'error'
          })
          return
        }

        setRecordedChunks([])
        const recorder = new MediaRecorder(stream, { mimeType })
        setMediaRecorder(recorder)

        recorder.ondataavailable = (event) => {
          if (event.data && event.data.size > 0) {
            setRecordedChunks((prev) => [...prev, event.data])
          }
        }

        recorder.onstart = () => {}
        recorder.onstop = () => {}
        recorder.onerror = () => {
          setSnackbar({
            open: true,
            message: t('userAnswersPage.videoRecordError'),
            severity: 'error'
          })
        }

        // Öni̇zleme zaten açık olmalı ama emin olalım
        if (videoRef.current && !videoRef.current.srcObject) {
          videoRef.current.srcObject = stream
          videoRef.current.muted = true
          videoRef.current.setAttribute('playsinline', 'true')
          videoRef.current.setAttribute('webkit-playsinline', 'true')
          try {
            await videoRef.current.play()
          } catch (e) {
            // iOS autoplay restriction, ignore
          }
        }

        recorder.start(1000)
        setIsRecording(true)

        setDisableStop(true)
        setTimeout(() => {
          setDisableStop(false)
        }, 2000)
      } catch (error) {
        setSnackbar({
          open: true,
          message: t('userAnswersPage.cameraError'),
          severity: 'error'
        })
      }
    } else {
      // STOP
      if (disableStop) {
        setSnackbar({
          open: true,
          message: t('userAnswersPage.shortRecordingWarn'),
          severity: 'warning'
        })
        return
      }
      try {
        if (mediaRecorder) {
          mediaRecorder.stop()
        }
        if (cameraStream) {
          cameraStream.getTracks().forEach((track) => track.stop())
        }
        setIsRecording(false)

        setTimeout(async () => {
          if (recordedChunks.length === 0) {
            setSnackbar({
              open: true,
              message: t('userAnswersPage.videoMandatoryError'),
              severity: 'error'
            })
            return
          }

          setMediaUploading(true)
          try {
            const mimeTypeUsed = mediaRecorder.mimeType
            const videoBlob = new Blob(recordedChunks, { type: mimeTypeUsed })

            if (!videoBlob || videoBlob.size === 0) {
              setSnackbar({
                open: true,
                message: t('userAnswersPage.videoMandatoryError'),
                severity: 'error'
              })
              setMediaUploading(false)
              return
            }

            const extension = mimeTypeUsed.includes('mp4') ? 'mp4' : 'webm'
            const fileType = mimeTypeUsed.includes('mp4') ? 'video/mp4' : 'video/webm'
            const file = new File([videoBlob], `recorded_video.${extension}`, { type: fileType })

            setRecordedChunks([])

            const remoteUrl = await uploadFile(file)
            setVideoPreviewUrl(remoteUrl)
            setSnackbar({
              open: true,
              message: t('userAnswersPage.videoRecordedMsg'),
              severity: 'success'
            })
          } catch (err) {
            setSnackbar({
              open: true,
              message: t('userAnswersPage.videoUploadError'),
              severity: 'error'
            })
          } finally {
            setMediaUploading(false)
          }
        }, 500)
      } catch (err) {
        setSnackbar({
          open: true,
          message: t('userAnswersPage.videoUploadError'),
          severity: 'error'
        })
      }
    }
  }

  const handleConfirmVideo = () => {
    setAnswersState((prev) => ({
      ...prev,
      [videoQuestionId]: {
        videoUrl: videoPreviewUrl
      }
    }))
    handleCancelVideo()
  }

  const handleCancelVideo = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop())
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null
      try {
        videoRef.current.pause()
      } catch (e) {
        // ignore
      }
      videoRef.current.removeAttribute('src')
      videoRef.current.load()
    }

    setCameraStream(null)
    setMediaRecorder(null)
    setRecordedChunks([])
    setVideoPreviewUrl(null)
    setVideoQuestionId(null)
    setIsRecording(false)
    setDisableStop(false)
    setVideoDialogOpen(false)
  }

  // ================================
  // PHOTO COLLAGE Handlers
  // ================================
  const openCollageEditor = (questionId) => {
    setCollageQuestionId(questionId)
    setCollageItems([])
    setCollageDialogOpen(true)
  }

  function fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => resolve(reader.result)
      reader.onerror = (error) => reject(error)
    })
  }

  async function handleAddCollageImage() {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*'
    input.multiple = true

    input.onchange = async () => {
      const files = Array.from(input.files || [])
      if (!files.length) return

      try {
        const newCollageItems = []
        for (let file of files) {
          const base64Data = await fileToBase64(file)
          newCollageItems.push({
            imageUrl: base64Data,
            x: 0,
            y: 0,
            width: 100,
            height: 100,
            rotation: 0
          })
        }
        setCollageItems((prev) => [...prev, ...newCollageItems])
      } catch (err) {
        setSnackbar({
          open: true,
          message: err.message || t('userAnswersPage.loadingDataError'),
          severity: 'error'
        })
      }
    }

    input.click()
  }

  const handleRemoveCollageItem = (index) => {
    setCollageItems((prev) => prev.filter((_, i) => i !== index))
  }

  const handleSaveCollage = async () => {
    try {
      if (collageRef.current?.clearSelection) {
        collageRef.current.clearSelection()
        await new Promise((resolve) => setTimeout(resolve, 50)) // ikonlar DOM'dan silinsin
      }

      if (!collageRef.current) {
        throw new Error(t('userAnswersPage.collageError'))
      }

      setMediaUploading(true)
      const canvas = await html2canvas(collageRef.current, {
        useCORS: true,
        scale: 2
      })
      const dataURL = canvas.toDataURL('image/png')
      const file = dataURLtoFile(dataURL, 'collage.png')

      const remoteUrl = await uploadFile(file)

      setAnswersState((prev) => ({
        ...prev,
        [collageQuestionId]: {
          collageSingleUrl: remoteUrl
        }
      }))

      setCollageDialogOpen(false)
      setCollageItems([])
      setCollageQuestionId(null)

      setSnackbar({
        open: true,
        message: t('userAnswersPage.collageSaveSuccess'),
        severity: 'success'
      })
    } catch (err) {
      setSnackbar({
        open: true,
        message: t('userAnswersPage.loadingDataError') + (err.message || err),
        severity: 'error'
      })
    } finally {
      setMediaUploading(false)
    }
  }

  const handleCancelCollage = () => {
    setCollageDialogOpen(false)
    setCollageItems([])
    setCollageQuestionId(null)
  }

  function dataURLtoFile(dataURL, filename) {
    const arr = dataURL.split(',')
    const mime = arr[0].match(/:(.*?);/)[1]
    const bstr = atob(arr[1])
    let n = bstr.length
    const u8arr = new Uint8Array(n)
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n)
    }
    return new File([u8arr], filename, { type: mime })
  }

  // ================================
  // AI MODERATOR: mesaj gönderme
  // ================================
  const handleAiModeratorSend = async (questionId, userText) => {
    try {
      // Önce local conversation'a user mesajını ekle
      const oldVal = answersState[questionId] || { conversation: [] }
      let newConversation = [...oldVal.conversation]

      if (newConversation.length === 0) {
        const qData = questions.find((q) => q.id === questionId)
        if (qData) {
          // Soru metninden HTML etiketlerini temizleyerek sadece düz metin alalım
          const questionTextOnly = qData.text.replace(/<[^>]*>/g, '')
          newConversation.push({ role: 'question', content: questionTextOnly })
        }
      }

      newConversation.push({ role: 'user', content: userText })

      setAnswersState((prev) => ({
        ...prev,
        [questionId]: {
          ...prev[questionId],
          conversation: newConversation
        }
      }))

      // Mesaj sayısını kontrol et
      const userMessageCount = newConversation.filter((m) => m.role === 'user').length
      const aiCount = questions.find((q) => q.id === questionId)?.aiCount || 0
      const aiCountPlusOne = aiCount + 1

      // Son kullanıcı mesaj hakkı dolmuşsa API çağrısı yapma
      if (userMessageCount === aiCountPlusOne) {
        return
      }

      // Normalde API'ye gidecek => spinner aç
      setAiLoading(true)

      const qData = questions.find((q) => q.id === questionId)
      if (!qData) return

      const aiPrompt = qData.aiPrompt || ''
      const bodyData = {
        prompt: aiPrompt,
        conversation: newConversation
      }

      const response = await fetch(
        'https://europe-west3-evosurvey.cloudfunctions.net/qip-evoo-comminity',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(bodyData)
        }
      )
      const result = await response.json()

      // Gelen cevabı asistan cevabı olarak ekle
      const assistantMsg = {
        role: 'assistant',
        content: result?.text || t('userAnswersPage.noAnswerformAI')
      }

      const finalConversation = [...newConversation, assistantMsg]

      setAnswersState((prev) => ({
        ...prev,
        [questionId]: {
          ...prev[questionId],
          conversation: finalConversation
        }
      }))

      await savePartialAnswers(activeStep)
    } catch (error) {
      setSnackbar({
        open: true,
        message: t('userAnswersPage.loadingDataError') + error.message,
        severity: 'error'
      })
    } finally {
      setAiLoading(false)
    }
  }

  // ================================
  // Device & timing helpers
  // ================================
  const getDeviceInfo = useCallback(() => {
    if (typeof navigator === 'undefined') {
      return {
        deviceType: 'unknown',
        userAgent: '',
        platform: '',
        language: '',
        vendor: ''
      }
    }

    const ua = navigator.userAgent || ''
    let deviceType = 'desktop'
    if (/ipad|tablet/i.test(ua)) {
      deviceType = 'tablet'
    } else if (/mobi|android|iphone/i.test(ua)) {
      deviceType = 'mobile'
    }

    return {
      deviceType,
      userAgent: ua,
      platform: navigator.platform || '',
      language: navigator.language || '',
      vendor: navigator.vendor || ''
    }
  }, [])

  const calculateSessionDurationMs = () => Math.max(Date.now() - sessionStartRef.current, 0)
  const getClientStartedAtIso = () => new Date(sessionStartRef.current).toISOString()

  // ================================
  // Partial (in-progress) save
  // ================================
  const savePartialAnswers = async (nextStepIndex) => {
    if (isPreviewMode) return
    try {
      const activeUserId = currentUser?.uid || responderId
      if (!activeUserId) return
      if (!questions.length) return
      if (!isResponsesFetched) return
      const progress = nextStepIndex
      if (!responseDocId) {
        const ref = await addDoc(collection(db, 'responses'), {
          userId: activeUserId,
          channelId,
          questionFormId,
          organizationId: formMeta?.organizationId || organization?.id || null,
          createdAt: serverTimestamp(),
          startedAt: serverTimestamp(),
          status: 'inProgress',
          progress,
          totalQuestions: questions.length,
          answers: answersState,
          clientStartedAt: getClientStartedAtIso(),
          deviceInfo: getDeviceInfo(),
          publicShareId: shareId || null,
          submittedVia: isPublicLink ? 'publicLink' : 'workspace'
        })
        setResponseDocId(ref.id)
      } else {
        await updateDoc(doc(db, 'responses', responseDocId), {
          status: 'inProgress',
          progress,
          totalQuestions: questions.length,
          answers: answersState,
          organizationId: formMeta?.organizationId || organization?.id || null,
          clientStartedAt: getClientStartedAtIso(),
          deviceInfo: getDeviceInfo(),
          publicShareId: shareId || null,
          submittedVia: isPublicLink ? 'publicLink' : 'workspace'
        })
      }
    } catch {}
  }

  // ================================
  // Required checks
  // ================================
  const checkIfRequiredQuestionAnswered = (q) => {
    if (!q.isRequired) return true
    const val = answersState[q.id]
    switch (q.questionType) {
      case 'openEnded': {
        const plainText = (val?.text || '').trim()
        return plainText.length > 0
      }
      case 'singleChoice': {
        if (q.allowOther && typeof val === 'object') {
          if (val.otherChecked) {
            return (val.otherText || '').trim().length > 0
          }
          return (val?.choice || '').length > 0
        }
        return !!val
      }
      case 'multipleChoice': {
        if (q.allowOther && typeof val === 'object') {
          const selectedCount = val.choices?.length || 0
          if (val.otherChecked) {
            return selectedCount > 0 || (val.otherText || '').trim().length > 0
          }
          return selectedCount > 0
        }
        return val && val.length > 0
      }
      case 'ratingQuestion':
        return val && val > 0
      case 'videoQuestion':
        return val?.videoUrl
      case 'photoCollage':
        return !!val?.collageSingleUrl
      case 'aiModerator':
        if (!val || !Array.isArray(val.conversation)) return false
        return val.conversation.some((m) => m.role === 'user')
      case 'infoQuestion':
        return true
      case 'cardSorting': {
        const assignments = val?.sorted || {};
        // Exclude 'unsorted' group from assigned cards count
        const assignedCards = Object.entries(assignments)
          .filter(([groupName]) => groupName !== 'unsorted')
          .flatMap(([_, cards]) => cards);
        return q.isRequired ? assignedCards.length === q.cards.length : true;
      }
      case 'screening': {
        if (q.screeningMode === 'multiple') {
          return Array.isArray(val) ? val.length > 0 : false
        }
        return typeof val === 'string' ? val.length > 0 : false
      }
      case 'pictureChoice': {
        if (q.pictureAllowOther && typeof val === 'object') {
          if (q.pictureAllowMultiple) {
            const selCount = (val.choices || []).length
            if (val.otherChecked) {
              return selCount > 0 || (val.otherText || '').trim().length > 0
            }
            return selCount > 0
          }
          if (val.otherChecked) {
            return (val.otherText || '').trim().length > 0
          }
          return (val.choice || '').length > 0
        }
        if (q.pictureAllowMultiple) {
          return Array.isArray(val) ? val.length > 0 : false
        }
        return typeof val === 'string' ? val.length > 0 : false
      }
      case 'masked': {
        const v = typeof val === 'string' ? val.trim() : ''

        // Boşsa ve zorunlu ise geçersiz
        if (!v) return false

        // Yes/No özel durumu
        if (q.maskedType === 'yesno') {
          return v === 'yes' || v === 'no'
        }

        // E‑posta validasyonu
        if (q.maskedType === 'email') {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
          return emailRegex.test(v)
        }

        // Sayısal değer validasyonu
        if (q.maskedType === 'number') {
          // Virgülü noktaya çevirerek de kabul etmeye çalışıyoruz
          const normalized = v.replace(',', '.')
          const num = Number(normalized)
          return !Number.isNaN(num)
        }

        // Tarih validasyonu (mask'ın ürettiği string'i Date ile parse etmeye çalışıyoruz)
        if (q.maskedType === 'date') {
          const d = new Date(v)
          return !Number.isNaN(d.getTime())
        }

        // Diğer tipler için sadece boş olmaması yeterli
        return v.length > 0
      }
      default:
        return true
    }
  }

  const findScreenoutRule = useCallback(
    (question, selectedValue) => {
      if (!question || question.questionType !== 'screening') return null
      const rules = Array.isArray(question.screeningRules) ? question.screeningRules : []
      if (!rules.length) return null

      const selectedArr = Array.isArray(selectedValue)
        ? selectedValue
        : selectedValue
          ? [selectedValue]
          : []

      const matched = rules.find((rule) => selectedArr.includes(rule.choice))
      if (!matched) return null

      return {
        choice: matched.choice,
        screenoutMessage: matched.screenoutMessage || matched.screenOutText || '',
        questionId: question.id,
        questionText: question.text || ''
      }
    },
    []
  )

  const handleScreenoutCompletion = async (question, matchedRule, nextAnswersState = answersState) => {
    if (!question || !matchedRule) return

    const info = {
      questionId: matchedRule.questionId || question.id,
      choice: matchedRule.choice,
      message: matchedRule.screenoutMessage || t('userAnswersPage.screenoutDefaultMessage'),
      questionText: matchedRule.questionText || question.text || ''
    }

    setScreenoutResult(info)

    if (isPreviewMode) return

    const activeUserId = currentUser?.uid || responderId
    if (!activeUserId || !channelId || !questionFormId) {
      setSnackbar({
        open: true,
        message: t('userAnswersPage.errorNoChannelOrForm'),
        severity: 'error'
      })
      return
    }

    setIsSubmitting(true)
    try {
      const progress = Math.min(activeStep + 1, questions.length)
      const finalAnswersArray = buildFinalAnswers(questions.slice(0, progress), nextAnswersState)

      const payload = {
        userId: activeUserId,
        channelId,
        questionFormId,
        organizationId: formMeta?.organizationId || organization?.id || null,
        status: 'screenedOut',
        completedAt: serverTimestamp(),
        progress,
        totalQuestions: questions.length,
        answers: nextAnswersState,
        finalAnswers: finalAnswersArray,
        publicShareId: shareId || null,
        submittedVia: isPublicLink ? 'publicLink' : 'workspace',
        screenout: info,
        clientStartedAt: getClientStartedAtIso(),
        durationMs: calculateSessionDurationMs(),
        deviceInfo: getDeviceInfo()
      }

      if (!responseDocId) {
        const ref = await addDoc(collection(db, 'responses'), {
          ...payload,
          createdAt: serverTimestamp(),
          startedAt: serverTimestamp()
        })
        setResponseDocId(ref.id)
      } else {
        await updateDoc(doc(db, 'responses', responseDocId), payload)
      }

      setHasAnswered(true)
    } catch (err) {
      setSnackbar({
        open: true,
        message: t('userAnswersPage.screenoutSaveError'),
        severity: 'error'
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  // ================================
  // Stepper next/back
  // ================================
  const handleNext = async () => {
    if (screenoutResult) return
    const currentQ = questions[activeStep]
    if (!checkIfRequiredQuestionAnswered(currentQ)) {
      setSnackbar({
        open: true,
        message: t('userAnswersPage.requiredQuestionError'),
        severity: 'error'
      })
      return
    }
    if (currentQ.questionType === 'openEnded' && currentQ.requireMedia) {
      const val = answersState[currentQ.id]
      if (!val?.media || val.media.length === 0) {
        setSnackbar({
          open: true,
          message: t('userAnswersPage.requiredMediaError'),
          severity: 'error'
        })
        return
      }
    }
    if (currentQ.questionType === 'videoQuestion') {
      const val = answersState[currentQ.id]
      if (!val?.videoUrl) {
        setSnackbar({
          open: true,
          message: t('userAnswersPage.requiredMediaMissing', {
            questionText: currentQ.text.replace(/<[^>]+>/g, '')
          }),
          severity: 'error'
        })
        return
      }
    }
    if (currentQ.questionType === 'photoCollage') {
      const val = answersState[currentQ.id]
      if (!val?.collageSingleUrl) {
        setSnackbar({
          open: true,
          message: t('userAnswersPage.requiredMediaMissing', {
            questionText: currentQ.text.replace(/<[^>]+>/g, '')
          }),
          severity: 'error'
        })
        return
      }
    }

    if (currentQ.questionType === 'screening') {
      const matched = findScreenoutRule(currentQ, answersState[currentQ.id])
      if (matched) {
        await handleScreenoutCompletion(currentQ, matched)
        return
      }
    }

    // MASKED özel validasyon
    if (currentQ.questionType === 'masked') {
      const v = typeof answersState[currentQ.id] === 'string'
        ? answersState[currentQ.id].trim()
        : ''

      let isValid = true

      if (!v) isValid = false

      if (currentQ.maskedType === 'email') {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        isValid = emailRegex.test(v)
      }

      if (currentQ.maskedType === 'number') {
        const normalized = v.replace(',', '.')
        const num = Number(normalized)
        isValid = !Number.isNaN(num)
      }

      if (currentQ.maskedType === 'date') {
        const d = new Date(v)
        isValid = !Number.isNaN(d.getTime())
      }

      if (!isValid) {
        setSnackbar({
          open: true,
          message: t('userAnswersPage.requiredQuestionError'),
          severity: 'error'
        })
        return
      }
    }

    await savePartialAnswers(activeStep + 1)
    setActiveStep((prev) => prev + 1)
  }

  const handleBack = () => {
    setActiveStep((prev) => prev - 1)
  }
  const isLastStep = activeStep === questions.length - 1

  const buildFinalAnswers = useCallback(
    (targetQuestions = questions, state = answersState) => {
      const finalAnswersArray = []
      targetQuestions.forEach((q) => {
        const val = state[q.id]
        let userAnswer = null

        switch (q.questionType) {
          case 'openEnded':
            userAnswer = {
              text: val?.text || '',
              media: val?.media || []
            }
            break
          case 'singleChoice':
            if (q.allowOther && typeof val === 'object') {
              if (val.otherChecked) {
                userAnswer = `Diğer: ${val.otherText || ''}`
              } else {
                userAnswer = val.choice
              }
            } else {
              userAnswer = val
            }
            break
          case 'multipleChoice':
            if (q.allowOther && typeof val === 'object') {
              const selectedArr = val.choices || []
              if (val.otherChecked) {
                userAnswer = [...selectedArr, `Diğer: ${val.otherText || ''}`]
              } else {
                userAnswer = selectedArr
              }
            } else {
              userAnswer = val
            }
            break
          case 'ratingQuestion':
            userAnswer = val
            break
          case 'videoQuestion':
            userAnswer = val?.videoUrl
            break
          case 'photoCollage':
            userAnswer = { collageSingleUrl: val?.collageSingleUrl || '' }
            break
          case 'aiModerator':
            userAnswer = val?.conversation || []
            break
          case 'infoQuestion':
            userAnswer = null
            break
          case 'cardSorting': {
            const sortedData = val?.sorted || {};
            const cardsWithText = {};
          
            Object.entries(sortedData).forEach(([groupName, cardIndices]) => {
              cardsWithText[groupName] = cardIndices.map(index => ({
                text: q.cards[index]?.text || '',
                imageUrl: q.cards[index]?.imageUrl || ''
              }));
            });
          
            userAnswer = cardsWithText;
            break;
          }
          case 'screening': {
            if (q.screeningMode === 'multiple') {
              userAnswer = Array.isArray(val) ? val : []
            } else {
              userAnswer = val || ''
            }
            break
          }
          case 'pictureChoice': {
            if (q.pictureAllowOther && typeof val === 'object') {
              if (q.pictureAllowMultiple) {
                const base = val.choices || []
                userAnswer = val.otherChecked
                  ? [...base, `Diğer: ${val.otherText || ''}`]
                  : base
              } else {
                userAnswer = val.otherChecked ? `Diğer: ${val.otherText || ''}` : val.choice
              }
            } else {
              userAnswer = val
            }
            break
          }
          case 'masked':
            userAnswer = val
            break
          default:
            userAnswer = val
            break
        }

        finalAnswersArray.push({
          questionId: q.id,
          questionText: q.text,
          answer: userAnswer
        })
      })

      return finalAnswersArray
    },
    [answersState, questions]
  )

  // ================================
  // Final submit
  // ================================
  const handleSubmitAnswers = async () => {
    if (!channelId || !questionFormId) {
      setSnackbar({
        open: true,
        message: t('userAnswersPage.noFormOrChannel'),
        severity: 'warning'
      })
      return
    }

    if (isPreviewMode) {
      setSnackbar({
        open: true,
        message: t('userAnswersPage.previewModeSubmitBlocked'),
        severity: 'info'
      })
      return
    }

    if (screenoutResult) {
      setSnackbar({
        open: true,
        message: t('userAnswersPage.screenoutAlready'),
        severity: 'info'
      })
      return
    }

    const activeUserId = currentUser?.uid || responderId
    if (!activeUserId) {
      setSnackbar({
        open: true,
        message: t('userAnswersPage.errorNoChannelOrForm'),
        severity: 'error'
      })
      return
    }

    setIsSubmitting(true)

    try {
      for (const q of questions) {
        if (!checkIfRequiredQuestionAnswered(q)) {
          setSnackbar({
            open: true,
            message: t('userAnswersPage.requiredQuestionMissing', {
              questionText: q.text.replace(/<[^>]+>/g, '')
            }),
            severity: 'error'
          })
          setIsSubmitting(false)
          return
        }
        if (q.questionType === 'openEnded' && q.requireMedia) {
          const val = answersState[q.id]
          if (!val?.media || val.media.length === 0) {
            setSnackbar({
              open: true,
              message: t('userAnswersPage.requiredMediaMissing', {
                questionText: q.text.replace(/<[^>]+>/g, '')
              }),
              severity: 'error'
            })
            setIsSubmitting(false)
            return
          }
        }
        if (q.questionType === 'videoQuestion') {
          const val = answersState[q.id]
          if (!val?.videoUrl) {
            setSnackbar({
              open: true,
              message: t('userAnswersPage.requiredMediaMissing', {
                questionText: q.text.replace(/<[^>]+>/g, '')
              }),
              severity: 'error'
            })
            setIsSubmitting(false)
            return
          }
        }
        if (q.questionType === 'photoCollage') {
          const val = answersState[q.id]
          if (!val?.collageSingleUrl) {
            setSnackbar({
              open: true,
              message: t('userAnswersPage.requiredMediaMissing', {
                questionText: q.text.replace(/<[^>]+>/g, '')
              }),
              severity: 'error'
            })
            setIsSubmitting(false)
            return
          }
        }
        if (q.questionType === 'screening') {
          const matched = findScreenoutRule(q, answersState[q.id])
          if (matched) {
            await handleScreenoutCompletion(q, matched)
            return
          }
        }
      }

      const finalAnswersArray = buildFinalAnswers()

      const sessionDuration = calculateSessionDurationMs()
      const clientStartedAtIso = getClientStartedAtIso()
      const deviceInfo = getDeviceInfo()
      const organizationIdForQuota = formMeta?.organizationId || organization?.id || null

      let shouldConsumeQuota = Boolean(organizationIdForQuota) && !responseDocId
      let previousResponseData = null

      if (organizationIdForQuota && responseDocId) {
        try {
          const existingSnap = await getDoc(doc(db, 'responses', responseDocId))
          if (existingSnap.exists()) {
            previousResponseData = existingSnap.data()
            shouldConsumeQuota = !previousResponseData?.countedInQuota
          }
        } catch (quotaReadError) {
          console.error('Response fetch error (quota check):', quotaReadError)
        }
      }

      if (!responseDocId) {
        await addDoc(collection(db, 'responses'), {
          userId: activeUserId,
          channelId,
          questionFormId,
          organizationId: formMeta?.organizationId || organization?.id || null,
          createdAt: serverTimestamp(),
          startedAt: serverTimestamp(),
          status: 'completed',
          completedAt: serverTimestamp(),
          progress: questions.length,
          totalQuestions: questions.length,
          answers: answersState,
          finalAnswers: finalAnswersArray,
          publicShareId: shareId || null,
          submittedVia: isPublicLink ? 'publicLink' : 'workspace',
          clientStartedAt: clientStartedAtIso,
          durationMs: sessionDuration,
          deviceInfo,
          countedInQuota: shouldConsumeQuota
        })
      } else {
        const responseUpdatePayload = {
          status: 'completed',
          completedAt: serverTimestamp(),
          progress: questions.length,
          answers: answersState,
          organizationId: formMeta?.organizationId || organization?.id || null,
          finalAnswers: finalAnswersArray,
          publicShareId: shareId || null,
          submittedVia: isPublicLink ? 'publicLink' : 'workspace',
          clientStartedAt: clientStartedAtIso,
          durationMs: sessionDuration,
          deviceInfo
        }

        if (shouldConsumeQuota || previousResponseData?.countedInQuota) {
          responseUpdatePayload.countedInQuota = true
        }

        await updateDoc(doc(db, 'responses', responseDocId), responseUpdatePayload)
      }

      if (shouldConsumeQuota && organizationIdForQuota) {
        try {
          await consumeResponseQuota(organizationIdForQuota)
        } catch (quotaErr) {
          console.error('Quota update error:', quotaErr)
        }
      }

      setSnackbar({
        open: true,
        message: t('userAnswersPage.answersSavedSuccess'),
        severity: 'success'
      })
      setHasAnswered(true)

      if (
        !isPreviewMode &&
        formMeta?.redirectEnabled &&
        formMeta?.redirectUrl
      ) {
        window.location.href = formMeta.redirectUrl
        return
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: t('userAnswersPage.answersSavedError'),
        severity: 'error'
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleStartNewPublicResponse = () => {
    sessionStartRef.current = Date.now()
    setHasAnswered(false)
    setScreenoutResult(null)
    setResponseDocId(null)
    setAnswersState({})
    setActiveStep(0)
    setIsResponsesFetched(false)
    setQuestions([])
    setUploadProgress(0)

    if (isPublicLink && !currentUser && shareId) {
      const storageKey = `publicResponder-${shareId}`
      const freshId = `anon-${shareId}-${Date.now().toString(36)}-${
        Math.random().toString(36).slice(2, 7)
      }`
      localStorage.setItem(storageKey, freshId)
      setResponderId(freshId)
    }

    setResetToken((prev) => prev + 1)
  }

  function CardSortingComponent({ question, answersState, setAnswersState }) {
    // Parent state içindeki sorted objesi
    const sortedData = answersState[question.id]?.sorted || {}

    // İlk açılışta tüm kartları "unsorted" altına koy ve grupları boş array olarak tanımla
    React.useEffect(() => {
      if (!sortedData.unsorted) {
        const cardsArray = Array.isArray(question.cards) ? question.cards : []
        const allCardIndices = cardsArray.map((_, idx) => idx)
        const newSorted = { unsorted: allCardIndices }

        if (Array.isArray(question.groups)) {
          question.groups.forEach((grp) => {
            newSorted[grp] = []
          })
        }

        setAnswersState((prev) => ({
          ...prev,
          [question.id]: {
            ...prev[question.id],
            sorted: newSorted
          }
        }))
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [question.id, question.cards, question.groups, sortedData.unsorted, setAnswersState])

    // Bir kartı bir listeden (fromKey) diğerine (toKey) taşımak için helper
    const moveCard = React.useCallback(
      (cardIndex, fromKey, toKey, toIndex = null) => {
        setAnswersState((prev) => {
          const prevSorted = prev[question.id]?.sorted || {}
          const fromArr = Array.isArray(prevSorted[fromKey]) ? [...prevSorted[fromKey]] : []
          const toArr = Array.isArray(prevSorted[toKey]) ? [...prevSorted[toKey]] : []

          const idxInFrom = fromArr.indexOf(cardIndex)
          if (idxInFrom === -1) {
            return prev
          }

          fromArr.splice(idxInFrom, 1)

          if (toIndex == null || toIndex < 0 || toIndex > toArr.length - 1) {
            toArr.push(cardIndex)
          } else {
            toArr.splice(toIndex, 0, cardIndex)
          }

          return {
            ...prev,
            [question.id]: {
              ...prev[question.id],
              sorted: {
                ...prevSorted,
                [fromKey]: fromArr,
                [toKey]: toArr
              }
            }
          }
        })
      },
      [question.id, setAnswersState]
    )

    // Grup içinde yukarı / aşağı taşıma
    const moveWithinGroup = (groupName, indexDelta, currentIndex) => {
      setAnswersState((prev) => {
        const prevSorted = prev[question.id]?.sorted || {}
        const groupArr = Array.isArray(prevSorted[groupName]) ? [...prevSorted[groupName]] : []
        const newIndex = currentIndex + indexDelta

        if (newIndex < 0 || newIndex >= groupArr.length) {
          return prev
        }

        const [item] = groupArr.splice(currentIndex, 1)
        groupArr.splice(newIndex, 0, item)

        return {
          ...prev,
          [question.id]: {
            ...prev[question.id],
            sorted: {
              ...prevSorted,
              [groupName]: groupArr
            }
          }
        }
      })
    }

    const handleAssignToGroup = (cardIndex, groupName) => {
      moveCard(cardIndex, 'unsorted', groupName)
    }

    const handleBackToUnsorted = (cardIndex, fromGroup) => {
      moveCard(cardIndex, fromGroup, 'unsorted')
    }

    const cards = Array.isArray(question.cards) ? question.cards : []
    const groups = Array.isArray(question.groups) ? question.groups : []

    return (
      <Container maxWidth="lg">
        <Typography variant="body1" sx={{ mb: 2 }}>
          {t('userAnswersPage.cardSortingIntro')}
        </Typography>

        {/* Unsorted alanı: sosyal medya story strip gibi */}
        <Box
          sx={{
            mb: 3,
            p: 2,
            borderRadius: 3,
            background:
              'linear-gradient(135deg, rgba(248,250,252,0.95), rgba(239,246,255,0.95))',
            border: '1px dashed rgba(148,163,184,0.6)'
          }}
        >
          <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 600, color: '#64748b' }}>
            {t('userAnswersPage.cardSortingUnsorted')}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: 1.5,
              overflowX: 'auto',
              pb: 1
            }}
          >
            {(sortedData.unsorted || []).map((cardIndex) => {
              const card = cards[cardIndex]
              if (!card) return null
              return (
                <Box
                  key={`unsorted-${cardIndex}`}
                  sx={{
                    minWidth: 160,
                    maxWidth: 180,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1
                  }}
                >
                  <CardItem card={card} />
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 0.5
                    }}
                  >
                    {groups.map((grpName) => (
                      <Button
                        key={grpName}
                        size="small"
                        variant="outlined"
                        onClick={() => handleAssignToGroup(cardIndex, grpName)}
                        sx={{
                          borderRadius: 999,
                          textTransform: 'none',
                          fontSize: 12,
                          px: 1.5
                        }}
                      >
                        {grpName}
                      </Button>
                    ))}
                  </Box>
                </Box>
              )
            })}
            {(!sortedData.unsorted || sortedData.unsorted.length === 0) && (
              <Typography
                variant="body2"
                sx={{ color: '#94a3b8', fontStyle: 'italic' }}
              >
                Tüm kartlar gruplara eklendi.
              </Typography>
            )}
          </Box>
        </Box>

        {/* Gruplar: her grup için kolon, içinde kartlar ve yukarı/aşağı/geri butonları */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            alignItems: 'stretch'
          }}
        >
          {groups.map((grpName) => (
            <Box
              key={grpName}
              sx={{
                flex: 1,
                p: 2,
                borderRadius: 3,
                backgroundColor: '#ffffff',
                border: '1px solid rgba(226,232,240,0.9)',
                boxShadow: '0 10px 24px rgba(15,23,42,0.06)'
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{ mb: 1.5, fontWeight: 700, color: '#0f172a' }}
              >
                {grpName}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {(sortedData[grpName] || []).map((cardIndex, index) => {
                  const card = cards[cardIndex]
                  if (!card) return null
                  const isFirst = index === 0
                  const isLast = index === (sortedData[grpName] || []).length - 1

                  return (
                    <Paper
                      key={`${grpName}-${cardIndex}`}
                      elevation={0}
                      sx={{
                        p: 1.5,
                        borderRadius: 2.5,
                        border: '1px solid rgba(148,163,184,0.35)',
                        background:
                          'linear-gradient(135deg, rgba(255,255,255,0.98), rgba(241,245,249,0.96))'
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5
                        }}
                      >
                        <Box sx={{ flexGrow: 1 }}>
                          <CardItem card={card} />
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 0.5
                          }}
                        >
                          <IconButton
                            size="small"
                            onClick={() => moveWithinGroup(grpName, -1, index)}
                            disabled={isFirst}
                          >
                            <ArrowUpwardIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => moveWithinGroup(grpName, 1, index)}
                            disabled={isLast}
                          >
                            <ArrowDownwardIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleBackToUnsorted(cardIndex, grpName)}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    </Paper>
                  )
                })}
                {(sortedData[grpName] || []).length === 0 && (
                  <Typography
                    variant="body2"
                    sx={{ color: '#94a3b8', fontStyle: 'italic' }}
                  >
                    Bu grupta henüz kart yok.
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      </Container>
    )
  }
  
   // Görsel kart tasarımı
  function CardItem({ card }) {
    return (
      <Paper
        sx={{
          width: 150,
          height: 100,
          p: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid #ccc'
        }}
      >
        {card.imageUrl && (
          <img
            src={card.imageUrl}
            alt="card"
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
          />
        )}
        {!card.imageUrl && (
          <Typography variant="body2" sx={{ textAlign: 'center' }}>
            {card.text}
          </Typography>
        )}
      </Paper>
    )
  }

  const totalQuestions = questions.length
  const answeredCount = totalQuestions ? Math.min(activeStep + 1, totalQuestions) : 0
  const progressValue = totalQuestions ? Math.round((answeredCount / totalQuestions) * 100) : 0
  const requiredCount = questions.filter((q) => q.isRequired).length
  const currentQuestionNumber = totalQuestions ? activeStep + 1 : 0
  const showNavigationArrows = formMeta?.showNavigationArrows !== false
  const showProgressBar = formMeta?.showProgressBar !== false
  const showQuestionNumber = formMeta?.showQuestionNumber !== false


  const currentQuestion = questions[activeStep] || null
  const choiceCardSx = (selected) => ({
    p: 1.25,
    borderRadius: 2.5,
    border: selected ? '1px solid #93c5fd' : '1px solid rgba(148,163,184,0.35)',
    background: selected ? 'linear-gradient(120deg, #eff6ff, #e0f2fe)' : '#fff',
    boxShadow: selected
      ? '0 18px 36px rgba(147,197,253,0.25)'
      : '0 12px 30px rgba(15,23,42,0.08)',
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    cursor: isSubmitting ? 'not-allowed' : 'pointer',
    transition: 'all 0.22s ease',
    '&:hover': {
      transform: isSubmitting ? 'none' : 'translateY(-2px)',
      boxShadow: selected
        ? '0 22px 44px rgba(147,197,253,0.3)'
        : '0 14px 34px rgba(15,23,42,0.1)'
    }
  })

  return (
    <>
      <Backdrop open={isSubmitting || mediaUploading} sx={{ color: '#fff', zIndex: 9999 }}>
        <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
          <CircularProgress color="inherit" />
          {/* Yüzde göstereceksek: */}
          {uploadProgress > 0 && uploadProgress < 100 && (
            <Box display="flex" flexDirection="column" alignItems="center">
              <Typography variant="body2" sx={{ mt: 2 }}>
                %{uploadProgress} 
              </Typography>
              <Box width={200}>
                <LinearProgress variant="determinate" value={uploadProgress} />
              </Box>
            </Box>
          )}
        </Box>
      </Backdrop>

      <Box
        sx={{
          minHeight: '100vh',
          background:
            'radial-gradient(circle at 22% 18%, rgba(252,165,165,0.18), transparent 28%), radial-gradient(circle at 78% 8%, rgba(125,211,252,0.18), transparent 26%), linear-gradient(180deg, #fdf2f8 0%, #eef2ff 55%, #e0f2fe 100%)',
          py: { xs: 2, md: 4 },
          px: { xs: 0, sm: 1, md: 3 }
        }}
      >
        <Container
          maxWidth="md"
          disableGutters
          sx={{
            pointerEvents: isSubmitting || mediaUploading ? 'none' : 'auto',
            overflow: 'auto',
            opacity: isSubmitting || mediaUploading ? 0.75 : 1,
            transition: 'opacity 0.2s ease',
            position: 'relative'
          }}
        >


        {isPreviewMode && (
          <Alert
            severity="info"
            icon={<VisibilityIcon fontSize="small" />}
            sx={{ mb: 2 }}
          >
            {t('userAnswersPage.previewModeBanner')}
          </Alert>
        )}

        {/* Kanal veya Form geçersiz */}
        {isResolvingLink ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
            <CircularProgress />
            <Typography variant="body1" sx={{ mt: 2 }}>
              {t('userAnswersPage.resolvingLink')}
            </Typography>
          </Box>
        ) : shareLinkError ? (
          <Paper sx={{ p: 3, mb: 4, borderRadius: 3, backgroundColor: '#fff3f3' }}>
            <Typography color="error" variant="h6" gutterBottom>
              {shareLinkError}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {t('userAnswersPage.publicLinkSupport')}
            </Typography>
          </Paper>
        ) : !channelId || !questionFormId ? (
          <Paper sx={{ p: 3, mb: 4, borderRadius: 3 }}>
            <Typography color="error">
              {t('userAnswersPage.noFormOrChannel')}
            </Typography>
            <Button variant="contained" onClick={() => navigate('/')} sx={{ mt: 2 }}>
              {t('userAnswersPage.formListReturn')}
            </Button>
          </Paper>
        ) : loadingQuestions || !isResponsesFetched ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
            <CircularProgress />
            <Typography variant="body1" sx={{ mt: 2 }}>
              {t('userAnswersPage.resolvingLink')}
            </Typography>
          </Box>
        ) : questionFormStatus && questionFormStatus !== 'published' && !isPreviewMode ? (
          <Paper sx={{ p: 3, mb: 4, borderRadius: 3 }}>
            <Typography color="error" variant="body1" gutterBottom>
              {t('userAnswersPage.formNotPublished')}
            </Typography>
            {!isPublicLink && (
              <Button variant="contained" onClick={() => navigate('/')} sx={{ mt: 2 }}>
                {t('userAnswersPage.formListReturn')}
              </Button>
            )}
          </Paper>
        ) : quotaExceeded ? (
          <Paper
            sx={{
              p: 3,
              mb: 4,
              borderRadius: 3,
              background: 'linear-gradient(135deg,#fef2f2,#fff1f2)'
            }}
          >
            <Typography color="error" variant="h6" gutterBottom>
              {t('userAnswersPage.quotaExceeded')}
            </Typography>
            {!isPublicLink && (
              <Button variant="contained" onClick={() => navigate('/')} sx={{ mt: 2 }}>
                {t('userAnswersPage.formListReturn')}
              </Button>
            )}
          </Paper>
        ) : screenoutResult ? (
          <Paper
            sx={{
              p: 3,
              mb: 4,
              borderRadius: 3,
              background: 'linear-gradient(135deg, #fff1f2, #fef2f2)'
            }}
          >
            <Typography color="error" variant="h6" gutterBottom>
              {t('userAnswersPage.screenoutTitle')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              {screenoutResult.message || t('userAnswersPage.screenoutDefaultMessage')}
            </Typography>
          </Paper>
        ) : hasAnswered && !isPreviewMode ? (
          <Paper sx={{ p: 3, mb: 4, borderRadius: 3, background: 'linear-gradient(135deg,#ecfeff,#f1f5f9)' }}>
            <Typography color="primary" variant="h6" gutterBottom>
              {t('userAnswersPage.alreadyAnswered')}
            </Typography>
            <Typography variant="body1" color="textSecondary">
              {isPublicLink
                ? t('userAnswersPage.alreadyAnsweredPublic')
                : t('userAnswersPage.alreadyAnsweredHint')}
            </Typography>
          </Paper>
        ) : loadingQuestions ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : questions.length === 0 ? (
          <Paper sx={{ p: 3, mb: 4, borderRadius: 3 }}>
            <Typography color="textSecondary">{t('userAnswersPage.noQuestions')}</Typography>
            <Divider sx={{ my: 2 }} />
            {!isPublicLink && (
              <Button variant="contained" onClick={() => navigate('/')}>
                {t('userAnswersPage.formListReturn')}
              </Button>
            )}
          </Paper>
        ) : (
          // Form Stepper
          <Paper
            sx={{
              p: { xs: 1.5, sm: 2, md: 3 },          // mobilde daha sıkı padding
              mb: { xs: 3, md: 4 },                 // mobilde daha kompakt margin
              borderRadius: { xs: 2, sm: 3, md: 4 }, // mobilde daha küçük radius
              background:
                'linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(226,232,240,0.9) 100%)',
              border: '1px solid rgba(148,163,184,0.35)',
              backdropFilter: 'blur(10px)'
            }}
          >
            {(showQuestionNumber || showProgressBar) && (
              <Box
                sx={{
                  mb: 2,
                  mt: -1,
                  pb: 1.5,
                  px: 1,
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 2
                }}
              >
                {showQuestionNumber && (
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: '#64748b',
                      letterSpacing: 0.2,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    ({answeredCount}/{totalQuestions})
                  </Typography>
                )}

                {showProgressBar && (
                  <Box sx={{ flexGrow: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={progressValue}
                      sx={{
                        height: 6,
                        borderRadius: 999,
                        backgroundColor: '#e2e8f0',
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 999,
                          background: 'linear-gradient(90deg, #93c5fd, #a5f3fc)'
                        }
                      }}
                    />
                  </Box>
                )}
              </Box>
            )}

            {/* Soru Alanı */}
            {currentQuestion && (
              <Box>
                <Paper
                  variant="outlined"
                  sx={{
                    p: { xs: 2, md: 3 },
                    mb: 2,
                    borderRadius: 3,
                    borderColor: 'rgba(148,163,184,0.35)',
                    background:
                      'linear-gradient(135deg, rgba(255,255,255,0.98), rgba(241,245,249,0.92))',
                    boxShadow: '0 14px 32px rgba(15,23,42,0.14)',
                    backdropFilter: 'blur(6px)'
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1} mb={1.5} flexWrap="wrap">                 
                    {currentQuestion.isRequired && (
                      <Chip
                        size="small"
                        color="error"
                        variant="outlined"
                        label={t('userAnswersPage.questionRequiredMark')}
                        sx={{ fontWeight: 600 }}
                      />
                    )}
                  </Box>
                  <Typography
                    variant="body1"
                    dangerouslySetInnerHTML={{ __html: currentQuestion.text || '' }}
                    sx={{
                      mb: 1,
                      '& img': {
                        maxWidth: '100%',
                        height: 'auto',
                        display: 'block'
                      },
                      '& .ql-align-center': { textAlign: 'center' },
                      '& .ql-align-right': { textAlign: 'right' },
                      '& .ql-align-justify': { textAlign: 'justify' }
                    }}
                  />
                </Paper>

              {currentQuestion && currentQuestion.questionType !== 'infoQuestion' && (

                <Paper
                  variant="outlined"
                  sx={{
                    p: { xs: 2, md: 3 },
                    borderRadius: 3,
                    borderColor: 'rgba(148,163,184,0.35)',
                    background:
                      'linear-gradient(135deg, rgba(255,255,255,0.96), rgba(241,245,249,0.92))',
                    boxShadow: '0 16px 36px rgba(15,23,42,0.12)',
                    backdropFilter: 'blur(6px)'
                  }}
                >
                  {(() => {
                    const q = currentQuestion
                    const qType = q.questionType || 'openEnded'
                    const val = answersState[q.id]

                    switch (qType) {
                      /**
                       * ====> Sadece Düz Metin Girişine Geçildi <====
                       * ReactQuill yerine TextField (multiline) kullanıyoruz.
                       */
                      case 'openEnded':
                        return (
                          <Box>
                            <TextField
                              multiline
                              minRows={4}
                              fullWidth
                              placeholder="Cevabınızı yazın..."
                              value={val?.text || ''}
                              onChange={(e) => handleOpenEndedTextChange(q.id, e.target.value)}
                              disabled={isSubmitting}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 3,
                                  backgroundColor: '#fff',
                                  boxShadow: '0 12px 32px rgba(15,23,42,0.12)',
                                  '& fieldset': { borderColor: 'rgba(148,163,184,0.4)' },
                                  '&:hover fieldset': { borderColor: '#93c5fd' },
                                  '&.Mui-focused fieldset': { borderColor: '#93c5fd', borderWidth: 2 }
                                }
                              }}
                            />
                            <Box
                              mt={2}
                              display="flex"
                              flexDirection={{ xs: 'column', sm: 'row' }}
                              alignItems={{ xs: 'stretch', sm: 'center' }}
                              gap={1.5}
                            >
                              <Button
                                variant="contained"
                                color="primary"
                                startIcon={<AddIcon />}
                                onClick={() => handleAddMedia(q.id)}
                                disabled={isSubmitting}
                                sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
                              >
                                {t('userAnswersPage.openEndedMediaAdd')}
                              </Button>
                              <Typography variant="body2" color="textSecondary">
                                {t('userAnswersPage.openEndedMediaAddHint')}                              
                              </Typography>
                            </Box>
                            <Box mt={2} display="flex" flexWrap="wrap" gap={2}>
                              {val?.media?.map((m, idx) => (
                                <Box
                                  key={idx}
                                  sx={{
                                    position: 'relative',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: 2,
                                    p: 1,
                                    backgroundColor: '#fff',
                                    boxShadow: '0 10px 28px rgba(15,23,42,0.08)'
                                  }}
                                >
                                  {m.fileType === 'image' && (
                                    <img
                                      src={m.remoteUrl}
                                      alt={`media-${idx}`}
                                      style={{ width: '100%', maxHeight: '220px', objectFit: 'contain', borderRadius: '8px' }}
                                    />
                                  )}
                                  {m.fileType === 'video' && (
                                    <video src={m.remoteUrl} controls style={{ width: '100%', maxHeight: '220px', objectFit: 'contain', borderRadius: '8px' }} />
                                  )}
                                  {m.fileType === 'audio' && (
                                    <audio src={m.remoteUrl} controls style={{ width: 220 }} />
                                  )}
                                  <IconButton
                                    size="large"
                                    onClick={() => handleRemoveMedia(q.id, idx)}
                                    sx={{
                                      position: 'absolute',
                                      top: 4,
                                      right: 4,
                                      color: 'red',
                                      backgroundColor: '#fff'
                                    }}
                                  >
                                    <DeleteIcon fontSize="large" />
                                  </IconButton>
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        )

                      case 'singleChoice':
                            if (q.allowOther && typeof val === 'object') {
                              return (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1.25
                                  }}
                                >
                                  {q.choices?.map((choice, idx) => {
                                    const selected = val?.choice === choice
                                    return (
                                      <Paper
                                        key={idx}
                                        onClick={() => {
                                          if (isSubmitting) return
                                          handleSingleChoiceChange(q.id, choice)
                                        }}
                                        sx={choiceCardSx(selected)}
                                      >
                                        <FormControlLabel
                                          control={
                                            <Radio
                                              checked={selected}
                                              onChange={(e) => {
                                                e.stopPropagation()
                                                handleSingleChoiceChange(q.id, choice)
                                              }}
                                              disabled={isSubmitting}
                                            />
                                          }
                                          label={choice}
                                          sx={{
                                            m: 0,
                                            width: '100%',
                                            '& .MuiFormControlLabel-label': {
                                              fontWeight: 700,
                                              color: '#0f172a'
                                            }
                                          }}
                                        />
                                      </Paper>
                                    )
                                  })}

                                  {/* Diğer seçeneği */}
                                  <Paper
                                    onClick={() => {
                                      if (isSubmitting) return
                                      setAnswersState((prev) => ({
                                        ...prev,
                                        [q.id]: {
                                          ...prev[q.id],
                                          choice: '',
                                          otherChecked: true
                                        }
                                      }))
                                    }}
                                    sx={choiceCardSx(val?.otherChecked)}
                                  >
                                    <FormControlLabel
                                      control={
                                        <Radio
                                          checked={val?.otherChecked === true}
                                          onChange={(e) => {
                                            e.stopPropagation()
                                            setAnswersState((prev) => ({
                                              ...prev,
                                              [q.id]: {
                                                ...prev[q.id],
                                                choice: '',
                                                otherChecked: true
                                              }
                                            }))
                                          }}
                                          disabled={isSubmitting}
                                        />
                                      }
                                      label={t('userAnswersPage.other')}
                                      sx={{
                                        m: 0,
                                        width: '100%',
                                        '& .MuiFormControlLabel-label': {
                                          fontWeight: 700,
                                          color: '#0f172a'
                                        }
                                      }}
                                    />
                                  </Paper>

                                  {val?.otherChecked && (
                                    <TextField
                                      size="small"
                                      fullWidth
                                      placeholder={t('userAnswersPage.otherDesc')}
                                      value={val?.otherText || ''}
                                      onChange={(e) => handleOtherTextChange(q.id, e.target.value)}
                                      onClick={(e) => e.stopPropagation()}
                                      disabled={isSubmitting}
                                      sx={{
                                        mt: 1,
                                        '& .MuiOutlinedInput-root': {
                                          borderRadius: 2,
                                          backgroundColor: '#fff',
                                          '& fieldset': { borderColor: 'rgba(148,163,184,0.4)' },
                                          '&:hover fieldset': { borderColor: '#93c5fd' },
                                          '&.Mui-focused fieldset': {
                                            borderColor: '#93c5fd',
                                            borderWidth: 2
                                          }
                                        }
                                      }}
                                    />
                                  )}
                                </Box>
                              )
                            } else {
                              return (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1.25
                                  }}
                                >
                                  {q.choices?.map((choice, idx) => {
                                    const selected = val === choice
                                    return (
                                      <Paper
                                        key={idx}
                                        onClick={() => {
                                          if (isSubmitting) return
                                          handleSingleChoiceChange(q.id, choice)
                                        }}
                                        sx={choiceCardSx(selected)}
                                      >
                                        <FormControlLabel
                                          control={
                                            <Radio
                                              checked={selected}
                                              onChange={(e) => {
                                                e.stopPropagation()
                                                handleSingleChoiceChange(q.id, choice)
                                              }}
                                              disabled={isSubmitting}
                                            />
                                          }
                                          label={choice}
                                          sx={{
                                            m: 0,
                                            width: '100%',
                                            '& .MuiFormControlLabel-label': {
                                              fontWeight: 700,
                                              color: '#0f172a'
                                            }
                                          }}
                                        />
                                      </Paper>
                                    )
                                  })}
                                </Box>
                              )
                            }

                      case 'multipleChoice':
                        if (q.allowOther && typeof val === 'object') {
                          const sel = val?.choices || []
                          const otherChk = val?.otherChecked

                          return (
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1.25
                              }}
                            >
                              {q.choices?.map((choice, idx) => {
                                const checked = sel.includes(choice)
                                return (
                                  <Paper
                                    key={idx}
                                    onClick={() => {
                                      if (isSubmitting) return
                                      handleMultipleChoiceChange(q.id, choice)
                                    }}
                                    sx={choiceCardSx(checked)}
                                  >
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        width: '100%'
                                      }}
                                    >
                                      <Checkbox
                                        checked={checked}
                                        disabled={isSubmitting}
                                      />
                                      <Typography
                                        variant="body1"
                                        sx={{
                                          fontWeight: 700,
                                          color: '#0f172a'
                                        }}
                                      >
                                        {choice}
                                      </Typography>
                                    </Box>
                                  </Paper>
                                )
                              })}

                              {/* Diğer seçeneği */}
                              <Box>
                                <Paper
                                  onClick={() => {
                                    if (isSubmitting) return
                                    toggleOtherChecked(q.id)
                                  }}
                                  sx={choiceCardSx(otherChk)}
                                >
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1,
                                      width: '100%'
                                    }}
                                  >
                                    <Checkbox
                                      checked={otherChk === true}
                                      disabled={isSubmitting}
                                    />
                                    <Typography
                                      variant="body1"
                                      sx={{
                                        fontWeight: 700,
                                        color: '#0f172a'
                                      }}
                                    >
                                      {t('userAnswersPage.other')}
                                    </Typography>
                                  </Box>
                                </Paper>

                                {otherChk && (
                                  <TextField
                                    size="small"
                                    fullWidth
                                    placeholder={t('userAnswersPage.otherDesc')}
                                    value={val?.otherText || ''}
                                    onChange={(e) => handleOtherTextChange(q.id, e.target.value)}
                                    disabled={isSubmitting}
                                    sx={{
                                      mt: 1,
                                      '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        backgroundColor: '#fff',
                                        '& fieldset': { borderColor: 'rgba(148,163,184,0.4)' },
                                        '&:hover fieldset': { borderColor: '#93c5fd' },
                                        '&.Mui-focused fieldset': {
                                          borderColor: '#93c5fd',
                                          borderWidth: 2
                                        }
                                      }
                                    }}
                                  />
                                )}
                              </Box>
                            </Box>
                          )
                        } else {
                          const arr = Array.isArray(val) ? val : []

                          return (
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1.25
                              }}
                            >
                              {q.choices?.map((choice, idx) => {
                                const checked = arr.includes(choice)
                                return (
                                  <Paper
                                    key={idx}
                                    onClick={() => {
                                      if (isSubmitting) return
                                      handleMultipleChoiceChange(q.id, choice)
                                    }}
                                    sx={choiceCardSx(checked)}
                                  >
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        width: '100%'
                                      }}
                                    >
                                      <Checkbox
                                        checked={checked}
                                        disabled={isSubmitting}
                                      />
                                      <Typography
                                        variant="body1"
                                        sx={{
                                          fontWeight: 700,
                                          color: '#0f172a'
                                        }}
                                      >
                                        {choice}
                                      </Typography>
                                    </Box>
                                  </Paper>
                                )
                              })}
                            </Box>
                          )
                        }

                      case 'pictureChoice': {
                        const choices = q.pictureChoices || []
                        const allowMultiple = q.pictureAllowMultiple
                        const allowOther = q.pictureAllowOther

                        const isSelected = (choiceLabel) => {
                          if (allowOther && typeof val === 'object') {
                            if (allowMultiple) {
                              return (val.choices || []).includes(choiceLabel)
                            }
                            return val.choice === choiceLabel
                          }
                          if (allowMultiple) {
                            return Array.isArray(val) && val.includes(choiceLabel)
                          }
                          return val === choiceLabel
                        }

                        const handleSelect = (choiceLabel) => {
                          if (allowMultiple) {
                            handlePictureMultiChange(q, choiceLabel)
                          } else {
                            handlePictureSingleChange(q, choiceLabel)
                          }
                        }

                        return (
                          <Box>
                            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                              {allowMultiple
                                ? t('userAnswersPage.screeningMultipleHint')
                                : t('userAnswersPage.screeningSingleHint')}
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                              {choices.map((choice, idx) => {
                                const selected = isSelected(choice.label)
                                return (
                                  <Paper
                                    key={idx}
                                    onClick={() => handleSelect(choice.label)}
                                    sx={{
                                      width: { xs: '100%', sm: 180 },
                                      cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                      border: selected ? '2px solid #93c5fd' : '1px solid rgba(148,163,184,0.4)',
                                      borderRadius: 2.5,
                                      overflow: 'hidden',
                                      boxShadow: selected
                                        ? '0 16px 32px rgba(147,197,253,0.25)'
                                        : '0 10px 24px rgba(15,23,42,0.08)',
                                      opacity: isSubmitting ? 0.7 : 1,
                                      transition: 'all 0.2s ease',
                                      transform: selected ? 'translateY(-2px)' : 'none',
                                      background: selected
                                        ? 'linear-gradient(135deg, #eff6ff, #e0f2fe)'
                                        : '#fff',
                                      '&:hover': {
                                        transform: isSubmitting ? 'none' : 'translateY(-3px)'
                                      }
                                    }}
                                  >
                                    <Box sx={{ height: 120, backgroundColor: '#f8fafc' }}>
                                      {choice.imageUrl ? (
                                        <img
                                          src={choice.imageUrl}
                                          alt={choice.label}
                                          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                        />
                                      ) : (
                                        <Box
                                          sx={{
                                            height: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#94a3b8'
                                          }}
                                        >
                                          {t('moderatorFormBuilder.uploadImage')}
                                        </Box>
                                      )}
                                    </Box>
                                    <Box sx={{ p: 1 }}>
                                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {choice.label}
                                      </Typography>
                                    </Box>
                                  </Paper>
                                )
                              })}
                            </Box>
                            {allowOther && typeof val === 'object' && (
                              <Box sx={{ mt: 1.5 }}>
                                <FormControlLabel
                                  control={
                                    allowMultiple ? (
                                      <Checkbox
                                        checked={val.otherChecked}
                                        onChange={() => toggleOtherChecked(q.id)}
                                        disabled={isSubmitting}
                                      />
                                    ) : (
                                      <Radio
                                        checked={val.otherChecked}
                                        onChange={() => toggleOtherChecked(q.id)}
                                        disabled={isSubmitting}
                                      />
                                    )
                                  }
                                  label={t('userAnswersPage.other')}
                                />
                                {val.otherChecked && (
                                  <TextField
                                    size="small"
                                    placeholder={t('userAnswersPage.otherDesc')}
                                    value={val.otherText || ''}
                                    onChange={(e) => handleOtherTextChange(q.id, e.target.value)}
                                    disabled={isSubmitting}
                                    sx={{ mt: 1, maxWidth: 320 }}
                                  />
                                )}
                              </Box>
                            )}
                          </Box>
                        )
                      }

                    case 'screening': {
                        const isMultiple = q.screeningMode === 'multiple'
                        const rules = Array.isArray(q.screeningRules) ? q.screeningRules : []
                        const isScreenoutOption = (choice) => rules.some((r) => r.choice === choice)
                        const selectedVal = val

                        const handleScreeningClick = (choice) => {
                          if (isSubmitting) return
                          if (isMultiple) {
                            handleScreeningMultipleChange(q, choice)
                          } else {
                            handleScreeningSingleChange(q, choice)
                          }
                        }

                        return (
                          <Box>
                            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                              {isMultiple
                                ? t('userAnswersPage.screeningMultipleHint')
                                : t('userAnswersPage.screeningSingleHint')}
                            </Typography>

                            {/* >>> singleChoice gibi DİKEY LİSTE <<< */}
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1.25
                              }}
                            >
                              {q.choices?.map((choice, idx) => {
                                const flagged = isScreenoutOption(choice)
                                const selected = isMultiple
                                  ? Array.isArray(selectedVal) && selectedVal.includes(choice)
                                  : selectedVal === choice

                                const baseCard = choiceCardSx(selected)
                                const optionSx = flagged
                                  ? {
                                      ...baseCard,                                   
                                    }
                                  : baseCard

                                return (
                                  <Paper
                                    key={idx}
                                    sx={optionSx}
                                    onClick={() => handleScreeningClick(choice)}
                                  >
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        width: '100%'
                                      }}
                                    >
                                      {isMultiple ? (
                                        <Checkbox
                                          checked={selected}
                                          disabled={isSubmitting}
                                        />
                                      ) : (
                                        <Radio
                                          checked={selected}
                                          disabled={isSubmitting}
                                        />
                                      )}

                                      <Typography
                                        variant="body1"
                                        sx={{
                                          fontWeight: 700,
                                         
                                        }}
                                      >
                                        {choice}
                                      </Typography>
                                    </Box>
                                  </Paper>
                                )
                              })}
                            </Box>
                          </Box>
                        )
                      }

                    case 'masked': {
                      const subtype = q.maskedType || 'number'
                      const commonProps = {
                        fullWidth: true,
                        size: 'small',
                        disabled: isSubmitting,
                        value: val || ''
                      }

                      if (subtype === 'yesno') {
                        return (
                          <Box>
                            <FormControlLabel
                              control={
                                <Radio
                                  checked={val === 'yes'}
                                  onChange={() => handleMaskedChange(q, 'yes')}
                                  disabled={isSubmitting}
                                />
                              }
                              label={t('userAnswersPage.yes')}
                            />
                            <FormControlLabel
                              control={
                                <Radio
                                  checked={val === 'no'}
                                  onChange={() => handleMaskedChange(q, 'no')}
                                  disabled={isSubmitting}
                                />
                              }
                              label={t('userAnswersPage.no')}
                            />
                          </Box>
                        )
                      }

                      const typeMap = {
                        number: 'number',
                        date: 'date',
                        email: 'email'
                      }
                      const inputType = typeMap[subtype] || 'text'

                      // Validation helpers and custom onChange for masked input
                      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
                      const numberRegex = /^[0-9]*$/
                      const dateRegex = /^\d{4}-\d{2}-\d{2}$/

                      const isInvalid = (() => {
                        if (!val) return false
                        const valueStr = String(val)
                        if (subtype === 'email') return !emailRegex.test(valueStr)
                        if (subtype === 'number') return !numberRegex.test(valueStr)
                        if (subtype === 'date') return !dateRegex.test(valueStr)
                        return false
                      })()

                      const handleMaskedInputChange = (e) => {
                        let inputValue = e.target.value || ''

                        if (subtype === 'number') {
                          // Sadece rakamlara izin ver
                          inputValue = inputValue.replace(/[^\d]/g, '')
                        }

                        if (subtype === 'date') {
                          // YYYY-MM-DD formatını aşan karakterleri kırp
                          inputValue = inputValue.slice(0, 10)
                        }

                        handleMaskedChange(q, inputValue)
                      }

                      return (
                        <TextField
                          {...commonProps}
                          type={inputType}
                          placeholder={t('userAnswersPage.maskedPlaceholder', { type: subtype })}
                          onChange={handleMaskedInputChange}
                          error={isInvalid}
                          helperText={
                            isInvalid
                              ? t('userAnswersPage.maskedInvalid', { type: subtype })
                              : ''
                          }
                        />
                      )
                    }

                      case 'ratingQuestion': {
                        const ratingButtons = []
                        for (let i = q.ratingMin; i <= q.ratingMax; i++) {
                          ratingButtons.push(i)
                        }
                        return (
                          <Box>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              {q.ratingName} (Min: {q.ratingMin}, Max: {q.ratingMax})
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {ratingButtons.map((num) => {
                                const selected = val === num
                                return (
                                  <Chip
                                    key={num}
                                    label={String(num)}
                                    onClick={() => handleRatingChange(q.id, num)}
                                    clickable
                                    disabled={isSubmitting}
                                    sx={{
                                      minWidth: 48,
                                      height: 42,
                                      fontWeight: 700,
                                      color: selected ? '#fff' : '#0f172a',
                                      backgroundColor: selected ? '#a5b4fc' : '#fff',
                                      border: selected
                                        ? '1px solid #93c5fd'
                                        : '1px solid rgba(148,163,184,0.4)',
                                      boxShadow: selected
                                        ? '0 14px 28px rgba(165,180,252,0.25)'
                                        : '0 10px 24px rgba(15,23,42,0.08)'
                                    }}
                                  />
                                )
                              })}
                            </Box>
                          </Box>
                        )
                      }

                    case 'videoQuestion':
                        return (
                          <Box>
                            {val?.videoUrl ? (
                              <Box sx={{ mb: 2 }}>
                                <video
                                  src={val.videoUrl}
                                  controls
                                  style={{
                                    width: '100%',
                                    maxHeight: '260px',
                                    objectFit: 'contain',
                                    borderRadius: '12px',
                                    boxShadow: '0 14px 28px rgba(15,23,42,0.25)'
                                  }}
                                />
                              </Box>
                            ) : (
                              <Typography variant="body2" sx={{ mb: 2 }}>
                                {t('userAnswersPage.videoNoPreview')}
                              </Typography>
                            )}

                            <Button
                              variant="contained"
                              onClick={() => handleVideoQuestion(q.id)}
                              disabled={isSubmitting}
                              startIcon={<VideocamIcon />}
                              sx={{
                                mt: val?.videoUrl ? 0 : 0.5,
                                borderRadius: 999,
                                px: 3,
                                py: 1.1,
                                fontWeight: 700,
                                textTransform: 'none',
                                boxShadow: '0 12px 28px rgba(15,23,42,0.22)',
                                background: 'linear-gradient(120deg, #4f46e5, #0ea5e9)',
                                '&:hover': {
                                  background: 'linear-gradient(120deg, #4338ca, #0284c7)'
                                }
                              }}
                            >
                              {t('userAnswersPage.startVideoRecord')}
                            </Button>
                          </Box>
                        )
                        
                      case 'photoCollage': {
                        const collageUrl = val?.collageSingleUrl
                        return (
                          <Box>
                            {collageUrl ? (
                              <Box sx={{ mb: 2 }}>
                                <img
                                  src={collageUrl}
                                  alt="collage-result"
                                  style={{ maxWidth: '100%', maxHeight: '260px',objectFit: 'contain', borderRadius: '8px',border: '1px solid #ccc' }}
                                />
                              </Box>
                            ) : (
                              <Typography variant="body2" sx={{ mb: 2 }}>
                                {t('userAnswersPage.collageNoPhoto')}
                              </Typography>
                            )}
                            <Button
                              variant="contained"
                              onClick={() => openCollageEditor(q.id)}
                              disabled={isSubmitting}
                              startIcon={<AddIcon />}
                              sx={{
                                mt: 1,
                                borderRadius: 999,
                                px: 3,
                                py: 1.1,
                                fontWeight: 700,
                                textTransform: 'none',
                                boxShadow: '0 12px 28px rgba(15,23,42,0.20)',
                                background: 'linear-gradient(120deg, #0ea5e9, #22d3ee)',
                                '&:hover': {
                                  background: 'linear-gradient(120deg, #0284c7, #06b6d4)'
                                }
                              }}
                            >
                              {t('userAnswersPage.collageEditor')}
                            </Button>
                          </Box>
                        )
                      }

                      case 'aiModerator':
                        return (
                          <AiModeratorChat
                            question={q}
                            conversation={val?.conversation || []}
                            onSendMessage={(userText) => handleAiModeratorSend(q.id, userText)}
                            isSubmitting={isSubmitting}
                            isAiLoading={aiLoading}
                          />
                        )

                      
                      case 'cardSorting':
                        return (
                          <CardSortingComponent
                            question={q}
                            answersState={answersState}
                            setAnswersState={setAnswersState}
                          />
                        );

                      default:
                        return null
                    }
                  })()}
                </Paper>
              )}
              </Box>
            )}

            <Box
              display="flex"
              justifyContent={showNavigationArrows ? 'space-between' : 'flex-end'}
              mt={3}
            >
              {showNavigationArrows && (
                <Button
                  disabled={activeStep === 0 || isSubmitting}
                  onClick={handleBack}
                  variant="outlined"
                  disableElevation
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    py: 1,
                    borderColor: 'rgba(148,163,184,0.6)',
                    color: '#0f172a',
                    fontWeight: 700
                  }}
                >
                  {t('userAnswersPage.back')}
                </Button>
              )}
              {isLastStep ? (
                <Button
                  variant="contained"
                  onClick={handleSubmitAnswers}
                  disabled={isSubmitting}
                  disableElevation
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    py: 1,
                    fontWeight: 800,
                    background: 'linear-gradient(120deg, #a5b4fc, #bae6fd)',
                    boxShadow: '0 14px 32px rgba(165,180,252,0.3)',
                    '&:hover': {
                      background: 'linear-gradient(120deg, #93a5f5, #a6dbf5)'
                    }
                  }}
                >
                  {isPreviewMode
                    ? t('userAnswersPage.previewModeButton')
                    : t('userAnswersPage.send')}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={isSubmitting}
                  disableElevation
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    py: 1,
                    fontWeight: 800,
                    background: 'linear-gradient(120deg, #93c5fd, #225b61ff)',
                    boxShadow: '0 14px 32px rgba(147,197,253,0.35)',
                    '&:hover': {
                      background: 'linear-gradient(120deg, #7fb4f7, #8ee0f2)'
                    }
                  }}
                >
                  {t('userAnswersPage.next')}
                </Button>
              )}
            </Box>
          </Paper>
        )}

        {/* Kolaj Dialog */}
      <Dialog
        open={collageDialogOpen}
        onClose={handleCancelCollage}
        fullWidth
        maxWidth="sm"
        disableEnforceFocus
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 18px 40px rgba(15,23,42,0.35)'
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            px: 2.5,
            py: 1.5,
            borderBottom: '1px solid rgba(148,163,184,0.3)'
          }}
        >
          <Box display="flex" flexDirection="column">
            <Typography variant="subtitle1" fontWeight={700}>
              {t('userAnswersPage.collageEditorTitle')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('userAnswersPage.collageEditorIntro')}
            </Typography>
          </Box>
          <Chip
            size="small"
            label={t('userAnswersPage.collageAddPhoto')}
            sx={{ fontWeight: 500 }}
          />
        </DialogTitle>

        <DialogContent
          sx={{
            pt: 2,
            pb: 1.5,
            px: 2.5,
            background:
              'linear-gradient(180deg, rgba(248,250,252,0.98), rgba(241,245,249,0.96))'
          }}
        >
          <Box sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddCollageImage}
              sx={{
                borderRadius: 999,
                px: 3,
                py: 1,
                textTransform: 'none',
                fontWeight: 600,
                width: '100%',
                justifyContent: 'center',
                borderColor: 'rgba(148,163,184,0.6)'
              }}
            >
              {t('userAnswersPage.collageAddPhoto')}
            </Button>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.75, display: 'block', textAlign: 'center' }}
            >
              {t('userAnswersPage.addedPhotos')}
            </Typography>
          </Box>
          {/* Eklenen fotoğraflar - thumbnail bar gibi */}
          {collageItems.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1.5,
                  maxHeight: 160,
                  overflowY: 'auto',
                  pb: 1
                }}
              >
                {collageItems.map((item, idx) => (
                  <Paper
                    key={idx}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      p: 1,
                      borderRadius: 2,
                      border: '1px solid rgba(148,163,184,0.4)',
                      backgroundColor: '#fff',
                      width: 88
                    }}
                  >
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: 1.5,
                        overflow: 'hidden',
                        mb: 0.75
                      }}
                    >
                      <img
                        src={item.imageUrl}
                        alt="collage"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    </Box>
                    <Button
                      variant="text"
                      color="error"
                      size="small"
                      onClick={() => handleRemoveCollageItem(idx)}
                      sx={{
                        textTransform: 'none',
                        fontSize: '0.7rem',
                        minWidth: 'auto',
                        p: 0
                      }}
                      startIcon={
                        <DeleteIcon
                          sx={{ fontSize: '1rem !important', mr: 0.25 }}
                        />
                      }
                    >
                      {t('userAnswersPage.collageRemove')}
                    </Button>
                  </Paper>
                ))}
              </Box>
            </Box>
          )}

          {/* Canvas alanı - sosyal medya yükleyicisi gibi kart içinde */}
          <Paper
            sx={{
              borderRadius: 3,
              border: '1px dashed rgba(148,163,184,0.7)',
              background:
                'radial-gradient(circle at top, rgba(191,219,254,0.10), transparent 60%), #fff',
              p: 2,
              mb: 2
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 1.5, textAlign: 'center' }}
            >
              {t('userAnswersPage.collageEditorIntro')}
            </Typography>
            <CollageCanvas
              ref={collageRef}
              collageItems={collageItems}
              setCollageItems={setCollageItems}
            />
          </Paper>

          
        </DialogContent>

        <DialogActions
          sx={{
            px: 2.5,
            py: 1.75,
            borderTop: '1px solid rgba(148,163,184,0.25)',
            backgroundColor: '#f8fafc'
          }}
        >
          <Button
            onClick={handleCancelCollage}
            color="inherit"
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {t('userAnswersPage.collageEditorCancel')}
          </Button>
          <Button
            onClick={handleSaveCollage}
            variant="contained"
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 999,
              px: 3,
              py: 0.75,
              boxShadow: '0 10px 26px rgba(37,99,235,0.25)'
            }}
          >
            {t('userAnswersPage.collageEditorSave')}
          </Button>
        </DialogActions>
      </Dialog>

 
        {/* Video Recording Dialog */}
        <Dialog
          open={videoDialogOpen}
          onClose={handleCancelVideo}
          fullWidth
          maxWidth="sm"
          PaperProps={{
            sx: {
              borderRadius: 3,
              overflow: 'hidden'
            }
          }}
        >
          <DialogTitle>{t('userAnswersPage.videoRecordTitle')}</DialogTitle>

          <DialogContent
            sx={{
              position: 'relative',
              pt: 1,
              pb: 7 // altta yuvarlak buton için boşluk
            }}
          >
            {/* Kırmızı REC etiketi */}
            {isRecording && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 12,
                  left: 16,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  color: 'error.main',
                  zIndex: 2
                }}
              >
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    bgcolor: 'error.main',
                    animation: 'blinker 1s linear infinite'
                  }}
                />
                <Typography variant="caption" color="error">
                  {t('userAnswersPage.videoRecording')}
                </Typography>
              </Box>
            )}

            {/* Kamera veya kayıt önizleme */}
            {!videoPreviewUrl ? (
              <video
                ref={videoRef}
                style={{
                  width: '100%',
                  borderRadius: 16,
                  backgroundColor: '#000',
                  objectFit: 'cover',
                  maxHeight: isMobile ? '60vh' : '70vh'
                }}
                playsInline
                muted
              />
            ) : (
              <Box
                component="video"
                src={videoPreviewUrl}
                controls
                sx={{
                  width: '100%',
                  borderRadius: 2,
                  backgroundColor: '#000',
                  maxHeight: isMobile ? '60vh' : '70vh',
                  objectFit: 'cover'
                }}
              />
            )}

            {/* Sosyal medya tarzı yuvarlak Başlat/Durdur butonu */}
            {!videoPreviewUrl && (
                <Box
                  sx={{
                    position: 'absolute',
                    left: '50%',
                    bottom: 16,
                    transform: 'translateX(-50%)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1,
                    zIndex: 3
                  }}
                >
                  <Fab
                    onClick={handleToggleRecording}
                    disabled={mediaUploading}
                    sx={{
                      width: 72,
                      height: 72,
                      boxShadow: '0 8px 20px rgba(0,0,0,0.35)',
                      bgcolor: isRecording ? 'error.main' : 'primary.main',
                      '&:hover': {
                        bgcolor: isRecording ? 'error.dark' : 'primary.dark'
                      }
                    }}
                  >
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: isRecording ? 1 : '50%',
                        bgcolor: '#fff',
                        transition: 'all 0.2s ease'
                      }}
                    />
                  </Fab>

                  <Typography variant="caption" sx={{ color: 'text.primary', fontWeight: 500 }}>
                    {isRecording
                      ? t('userAnswersPage.videoStopAndAnswerLabel')
                      : t('userAnswersPage.videoStartAndAnswerLabel')}
                  </Typography>
                </Box>             
              )}
          </DialogContent>

          <DialogActions
            sx={{
              px: 3,
              pb: 2,
              pt: 1,
              justifyContent: 'space-between'
            }}
          >
            <Button onClick={handleCancelVideo} color="inherit">
              {t('userAnswersPage.videoDialogCancel')}
            </Button>
            <Button
              onClick={handleConfirmVideo}
              variant="contained"
              disabled={!videoPreviewUrl || isRecording || mediaUploading}
            >
              {t('userAnswersPage.videoDialogConfirm')}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
        </Container>
      </Box>
    
    </>
  )
}
